import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import { api, wsUrl, getSessionToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { sortByRank, sortBySuit, sortByValue, sortGroupCards, layoutSequence, sequenceJokerEnds } from "../lib/cardSort";
import { matStyle, useGameTableTheme } from "../lib/tableThemes";
import { useTopBanner, preloadEndOfGameAd, showEndOfGameAd } from "../lib/ads";
import { buildPlayerColorMap } from "../lib/playerColors";
import { boardCollision, freesSetJoker } from "../lib/tableDrop";
import { useTurnClock, msLeft } from "../lib/turnClock";
import { seatsFrom } from "../lib/seating";
import { haptic, sound, isMuted, setMuted, ensureNotificationPermission, notifyYourTurn } from "../lib/feedback";
import { toast } from "../lib/notify";
import { t, tList, trServer, useLang } from "../lib/i18n";
import { PlayingCard } from "../components/PlayingCard";
import { GameTopBar } from "../components/game/GameTopBar";
import { OpponentsRow } from "../components/game/OpponentsRow";
import { PiocheArea } from "../components/game/PiocheArea";
import { TableCombinations } from "../components/game/TableCombinations";
import { ScoreboardOverlay } from "../components/game/ScoreboardOverlay";
import { BottomActions } from "../components/game/BottomActions";
import { PlayerHand } from "../components/game/PlayerHand";
import { RoundSummaryDialog } from "../components/game/RoundSummaryDialog";
import { BrokenSetsLayer, useBrokenSets } from "../components/game/BrokenSets";
import { gameWinners } from "../lib/roundSummary";
import { COLUMN_LIFT, floatWidth, usePhoneBoard, useTableRoom } from "../lib/tableRoom";
import { JokerInsertDialog } from "../components/game/JokerInsertDialog";
import { RedealVoteSheet, RedealVoteCapsule } from "../components/game/RedealtBanner";
import { PosePreview } from "../components/game/PosePreview";
import { WinnerCelebration } from "../components/game/WinnerCelebration";
import { GameFeed } from "../components/game/GameFeed";
import { CardFlyLayer } from "../components/game/CardFlyAnimation";
import { EmojiLayer, EmojiPicker, useEmojiReactions } from "../components/game/EmojiReactions";
import { FirstGameTutorial } from "../components/tutorial/TutorialDialog";
import { EMOJI_REACTIONS, TUTORIAL } from "../lib/features";

// Idle nudge: first reminder after NUDGE_AFTER_MS without any interaction on
// your turn, then every NUDGE_REPEAT_MS, at most NUDGE_MAX times.
const NUDGE_AFTER_MS = 25000;
const NUDGE_REPEAT_MS = 30000;
const NUDGE_MAX = 3;

// The redeal vote's sheet comes down over the hidden Dealer's room (its avatar, h-9 + gap-1), 10 px
// above the bar; the block (sheet, bar, hand) takes VOTE_SLIDE_S to go up to the middle and back.
const VOTE_SHEET_OVERLAP = 30;
const VOTE_SLIDE_S = 0.6;

// The server's sentence for its rule « two Jokers never side by side in a run »
// (JOKERS_SIDE_BY_SIDE in backend/game/combinations.py), translated by trServer.
const JOKERS_SIDE_BY_SIDE = "Deux Jokers ne peuvent pas se suivre dans une suite";

export default function Game() {
  const { roomId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useLang();
  // This player's own table colour (Lobby "Tapis" button or waiting room). One unlocked with a
  // video stays until the page leaves, even if its hour runs out during the game.
  const tableThemeId = useGameTableTheme();
  const bannerHeight = useTopBanner(); // native ad banner (apps only), drawn over the top of the web view
  const emojis = useEmojiReactions(roomId, user?.user_id);
  const { receive: receiveEmoji } = emojis;

  const [state, setState] = useState(null);
  const wsRef = useRef(null);
  // When this game's page opened (Game is keyed by the room): the end-of-game ad skips short games.
  const openedAtRef = useRef(Date.now());
  const [wsVersion, setWsVersion] = useState(0);
  const lastVersionRef = useRef(-1);

  // UI state
  const [selected, setSelected] = useState(new Set());
  const [pendingGroups, setPendingGroups] = useState([]);
  // Additions staged locally during a turn — cards from the player's hand
  // destined to extend an ALREADY-POSED combination (own or opponent's).
  // Committed atomically via /discard alongside pendingGroups + final discard.
  // Shape: [{ target_user_id, combination_id, card_ids: [...] }]
  const [pendingAdditions, setPendingAdditions] = useState([]);
  // Joker swaps staged locally during a turn — performed pre-Nemna3 once the
  // composed combinations meet the threshold. Committed atomically via
  // /discard alongside pendingGroups + pendingAdditions + final discard.
  // Shape: [{ target_user_id, combination_id, joker_card_id,
  //           replacement_card_id, additional_card_id? }]
  const [pendingReplacements, setPendingReplacements] = useState([]);
  // Joker recoveries already committed this turn (immediate /replace-joker call).
  // Shape: [{ target_user_id, combination_id, joker_card_id,
  //           replacement_card_id, additional_card_id? }]
  // Kept until the turn ends so the player can undo via /undo-joker.
  const [recoveredJokers, setRecoveredJokers] = useState([]);
  // Additions committed this turn via /add-to-combo (has_played_nemna3=true path).
  // Shape: [{ target_user_id, combination_id, card_ids: [...] }]
  // Kept until discard so the player can undo via /undo-addition.
  const [committedAdditions, setCommittedAdditions] = useState([]);
  const showRoundSummary = state?.game?.phase === "round_over" || state?.game?.phase === "game_over";
  const [sortMode, setSortMode] = useState("manual"); // "manual" | "rank" | "suit"
  const [manualOrder, setManualOrder] = useState([]);
  const [activeDrag, setActiveDrag] = useState(null);
  const [activeDropComboId, setActiveDropComboId] = useState(null);
  // Client-side staging for "Take Fausse + Nemna3": the top discard card is
  // visually moved to the hand while the player prepares. On cancel it returns
  // to the Fausse (no penalty, since nothing is committed to backend).
  const [takenDiscardCard, setTakenDiscardCard] = useState(null);
  // Modal contextuel pour l'insertion d'un Joker sur une SUITE existante.
  // null = fermé. {ownerId, comboId, jokerCardId} = ouvert.
  const [jokerInsertChoice, setJokerInsertChoice] = useState(null);
  const [muted, setMutedState] = useState(isMuted());
  const toggleMute = () => { const m = !muted; setMuted(m); setMutedState(m); };
  const [voting, setVoting] = useState(false);
  // { [user_id]: boolean } — qui parle au micro en ce moment (affiché dans ScoreboardOverlay)
  const [voiceSpeaking, setVoiceSpeaking] = useState({});
  // { [user_id]: "connecting" | "connected" | cause d'échec } — état de la liaison vocale avec chaque joueur
  const [voiceLinks, setVoiceLinks] = useState({});
  // Clé incrémentée à chaque redistribution → force remontage des cartes en main
  // pour rejouer l'animation de flip "nouvelles cartes distribuées".
  const [dealingKey, setDealingKey] = useState(0);
  const [isDealing, setIsDealing] = useState(false);
  const [backingToLobby, setBackingToLobby] = useState(false);
  const [rematching, setRematching] = useState(false);

  const feedRef = useRef(null);
  const feed = useCallback((text, type = "info", opts) => feedRef.current?.push(text, type, opts), []);

  // Error wrapper: haptic + sound + feed notification.
  const errToast = useCallback((msg) => {
    haptic.error(); sound.error();
    feedRef.current?.push(msg, "error");
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );
  // The table column scrolls: groups out of its sight take no drop.
  const tableScrollRef = useRef(null);
  const tableListRef = useRef(null);
  const pileColumnRef = useRef(null);
  // A single card dragged (usually a discard) never lights up the new-group zone.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const collisionDetection = useMemo(
    () => boardCollision(
      () => tableScrollRef.current?.getBoundingClientRect(),
      (id) => !(selectedRef.current.has(id) && selectedRef.current.size > 1),
    ),
    [],
  );

  // Refetch full state via REST. Used after reconnect / tab refocus.
  const refetchState = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}`);
      const v = data?.state?.game?.version;
      if (typeof v === "number" && v >= lastVersionRef.current) {
        lastVersionRef.current = v;
        setState(data.state);
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        wsClosedByUserRef.current = true;
        navigate("/lobby", { replace: true });
      }
    }
  }, [roomId, navigate]);

  // Lock body scroll while in the game view. On mobile browsers, body height
  // is based on the maximum viewport (chrome retracted), so when the address
  // bar reappears the body overflows and scrolls, hiding the header.
  useEffect(() => {
    const el = document.documentElement;
    const prev = el.style.overflow;
    el.style.overflow = "hidden";
    return () => { el.style.overflow = prev; };
  }, []);

  // WS connection with auto-reconnect + exponential backoff. Survives mobile
  // background sleeps, network glitches, and proxy idle timeouts.
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const wsClosedByUserRef = useRef(false);

  const connect = useCallback(() => {
    const token = getSessionToken();
    if (!token) return null;
    // One socket at a time. Several triggers can ask to reconnect at the same
    // moment (focus + visibilitychange + online, heartbeat, a close handler):
    // a second socket would be left open, unpinged, and voice signaling
    // arriving on it would be lost.
    const current = wsRef.current;
    if (current && (current.readyState === 0 || current.readyState === 1)) return current;
    let ws;
    try {
      ws = new WebSocket(wsUrl(roomId, token));
    } catch {
      return null;
    }
    wsRef.current = ws;
    setWsVersion((v) => v + 1);
    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      // Server pushes initial state on connect; we also re-fetch via REST as
      // a safety net to immediately restore the latest version after a gap.
      refetchState();
    };
    ws.onmessage = (ev) => {
      try {
        if (ev.data === "pong") return;
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          const v = msg.data?.game?.version;
          if (typeof v === "number") {
            if (v < lastVersionRef.current) return;
            lastVersionRef.current = v;
          }
          setState(msg.data);
        } else if (msg.type === "player_quit") {
          feed(t("game.ws.quit", { name: msg.name }), "penalty");
        } else if (msg.type === "emoji") {
          receiveEmoji(msg);
        } else if (msg.type === "error") {
          wsClosedByUserRef.current = true;
          const isPersonal = msg.reason === "replaced_by_bot" || msg.reason === "quit";
          navigate(isPersonal ? "/" : "/lobby", { replace: true });
        }
      } catch {}
    };
    ws.onclose = () => {
      if (wsClosedByUserRef.current) return;
      // Exponential backoff: 1s, 2s, 4s, max 8s
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const delay = Math.min(1000 * 2 ** (attempt - 1), 8000);
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
    ws.onerror = () => {
      try { ws.close(); } catch {}
    };
    return ws;
  }, [roomId, refetchState, receiveEmoji]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirectUrl = window.location.pathname + window.location.search;
      window.location.href = `/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    refetchState();
    wsClosedByUserRef.current = false;
    connect();

    // Heartbeat — keeps proxies/Capacitor alive
    const ping = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === 1) {
        try { ws.send("ping"); } catch {}
      } else if (ws?.readyState !== 0) {
        // Not connecting and not open — reconnect ASAP
        connect();
      }
    }, 15000);

    // Reconnect + resync when tab becomes visible again (mobile/desktop)
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) connect();
        refetchState();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);

    return () => {
      clearInterval(ping);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
      wsClosedByUserRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const ws = wsRef.current;
      if (ws) {
        try { ws.close(); } catch {}
      }
    };
  }, [loading, user, roomId, connect, refetchState, navigate]);


  // Action log is now handled by GameFeed — no toasts needed here.

  // Notification permission ("C'est ton tour !" while the app is in the background): asked on
  // the first tap, and only in a game with another human player — against bots alone the
  // turn comes back at once, so the request would be useless (owner's choice).
  const withOtherHumans = !!state?.game?.players?.some((p) => !p.is_bot && p.user_id !== user?.user_id);
  useEffect(() => {
    if (!withOtherHumans) return undefined;
    const handler = () => ensureNotificationPermission();
    window.addEventListener("pointerdown", handler, { once: true });
    return () => window.removeEventListener("pointerdown", handler);
  }, [withOtherHumans]);

  // Detect turn handover -> sound + local notification (no vibration: the owner removed it).
  const prevTurnRef = useRef(null);
  useEffect(() => {
    const cpid = state?.game?.current_player_id;
    const myId = user?.user_id;
    if (!cpid || !myId) return;
    const prev = prevTurnRef.current;
    if (prev !== null && prev !== cpid && cpid === myId) {
      // Turn just became mine
      sound.yourTurn();
      notifyYourTurn(state?.game?.round_number || 1);
    }
    prevTurnRef.current = cpid;
  }, [state?.game?.current_player_id, state?.game?.round_number, user?.user_id]);

  // Detect own hand-size deltas -> draw/discard/combo sounds.
  // ALSO: when player draws a new card, auto-select it if the user is in the
  // middle of composing (pendingGroups non-empty) so they can immediately see
  // the new card highlighted and group it.
  // Runs on every change of the cards held, not only of their number: after a
  // Joker swap (or its undo) the count stays the same, and the ids remembered
  // must still follow, or the next card to come back is mistaken for another.
  const prevHandLenRef = useRef(null);
  const prevHandIdsRef = useRef([]);
  const handIdsKey = (state?.game?.my_hand || []).map((c) => c.id).join(",");
  useEffect(() => {
    const cur = state?.game?.my_hand?.length;
    if (cur === undefined) return;
    const prev = prevHandLenRef.current;
    const curIds = (state?.game?.my_hand || []).map((c) => c.id);
    if (prev !== null && prev !== undefined) {
      if (cur === prev + 1) {
        haptic.light();
        // A draw adds one card to those held. A new deal replaces them all (14 → 15: the 15 cards came
        // to this player, after a redeal or at a new round): no card to select, the sort stays.
        const newIds = curIds.filter((id) => !prevHandIdsRef.current.includes(id));
        const newCardId = newIds.length === 1 ? newIds[0] : null;
        if (newCardId) {
          // Auto-select
          setSelected((prev) => new Set([...prev, newCardId]));
          // Insert at middle of current displayed order, regardless of sort mode.
          // myHand already includes the new card (sorted in or appended at end).
          // Remove it, find the midpoint, reinsert there, then lock to manual.
          const displayIds = myHand.map((c) => c.id);
          const withoutNew = displayIds.filter((id) => id !== newCardId);
          const mid = Math.ceil(withoutNew.length / 2);
          setManualOrder([...withoutNew.slice(0, mid), newCardId, ...withoutNew.slice(mid)]);
          setSortMode("manual");
        }
      } else if (cur === prev - 1) {
        haptic.light();
      } else if (cur < prev - 1) {
        sound.combo(); haptic.medium();
      }
    }
    prevHandLenRef.current = cur;
    prevHandIdsRef.current = curIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handIdsKey]);

  // Poll every 3 s during round_over so non-host players catch the "Manche suivante"
  // transition even if their WebSocket drops between heartbeat ticks.
  useEffect(() => {
    if (!showRoundSummary) return;
    const t = setInterval(() => { refetchState(); }, 3000);
    return () => clearInterval(t);
  }, [showRoundSummary, refetchState]);

  // Round / game over sounds.
  const prevPhaseRef = useRef(null);
  useEffect(() => {
    const phase = state?.game?.phase;
    if (!phase) return;
    const prev = prevPhaseRef.current;
    if (prev && prev !== phase) {
      if (phase === "round_over") {
        const won = state?.game?.winner_id === user?.user_id;
        if (won) { sound.victory(); haptic.success(); }
        else { sound.defeat(); haptic.warning(); }
      } else if (phase === "game_over") {
        const won = gameWinners(state?.game).includes(user?.user_id);
        if (won) { sound.victory(); haptic.heavy(); }
        else { sound.defeat(); haptic.warning(); }
      }
    }
    prevPhaseRef.current = phase;
  }, [state?.game?.phase, state?.game?.winner_id, state?.game?.game_winner_id, user?.user_id]);

  // Card fly animations
  const [flyCards, setFlyCards] = useState([]);
  const flyUidRef = useRef(0);

  const drewAnimShownRef  = useRef(null);
  const animQueueEndRef  = useRef(0); // wall-clock ms when last queued bot animation ends

  const triggerFly = useCallback((fromId, toId, card = null, type = "fly") => {
    const getCenter = (id) => {
      if (id === "player-hand") return { x: window.innerWidth / 2, y: window.innerHeight - 90 };
      const el = document.getElementById(id);
      if (!el || el.offsetParent === null) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };
    const from = getCenter(fromId);
    const to = getCenter(toId);
    if (!from || !to) return;
    if (type !== "draw" && type !== "discard") sound.cardFly();
    const uid = ++flyUidRef.current;
    setFlyCards((prev) => [
      ...prev,
      { id: uid, from, to, card, type, onDone: () => setFlyCards((p) => p.filter((a) => a.id !== uid)) },
    ]);
  }, []);

  // Detect opponent / bot card moves for fly animations
  const prevAnimPhaseRef = useRef(null);
  const prevAnimPlayerRef = useRef(null);
  useEffect(() => {
    const phase = state?.game?.phase;
    const cpid = state?.game?.current_player_id;
    const myId = user?.user_id;
    if (!phase || !cpid || !myId) return;
    const prevPhase = prevAnimPhaseRef.current;
    const prevCpid = prevAnimPlayerRef.current;

    if (prevPhase !== null) {
      // Human opponent drew (we can see intermediate drawing→acting state)
      if (prevPhase === "drawing" && phase === "acting" && cpid !== myId && cpid === prevCpid) {
        const drewFromDiscard = !!state?.game?.players?.find(
          (p) => p.user_id === cpid
        )?.preview_state?.took_discard;
        triggerFly(drewFromDiscard ? "defausse-pile" : "pioche-pile", `opponent-avatar-${cpid}`, null, "draw");
        drewAnimShownRef.current = cpid;
      }

      // Player changed → human opponent finished their turn (bot animations removed)
      if (prevCpid && prevCpid !== cpid && prevCpid !== myId) {
        if (drewAnimShownRef.current === prevCpid) {
          triggerFly(`opponent-avatar-${prevCpid}`, "defausse-pile", state?.game?.discard_top ?? null, "discard");
          drewAnimShownRef.current = null;
        }
      }
    }

    prevAnimPhaseRef.current = phase;
    prevAnimPlayerRef.current = cpid;
  }, [state?.game?.phase, state?.game?.current_player_id, user?.user_id, triggerFly]);

  // Detect when another player's preview_state.took_discard flips true →
  // trigger the défausse→avatar animation immediately (staging path: phase
  // stays "drawing" server-side, so the drawing→acting trigger above never fires).
  const prevTookDiscardRef = useRef({});
  useEffect(() => {
    const myId = user?.user_id;
    const players = state?.game?.players || [];
    players.forEach((p) => {
      if (p.user_id === myId) return; // skip self
      const tookNow = !!p.preview_state?.took_discard;
      const tookBefore = !!prevTookDiscardRef.current[p.user_id];
      if (tookNow && !tookBefore) {
        // Player just staged a discard take — fly card from défausse to their avatar
        triggerFly("defausse-pile", `opponent-avatar-${p.user_id}`, null, "draw");
      }
      prevTookDiscardRef.current[p.user_id] = tookNow;
    });
  }, [state?.game?.players, user?.user_id, triggerFly]);

  // Broadcast transient events (e.g. brelan discarded). The backend bumps
  // `event_seq` each time a noteworthy event occurs and attaches a compact
  // payload to `last_event`. We display a transient toast for every new seq.
  const lastEventSeqRef = useRef(0);
  useEffect(() => {
    const ev = state?.game?.last_event;
    const seq = state?.game?.event_seq;
    if (!ev || !seq || seq <= lastEventSeqRef.current) return;
    lastEventSeqRef.current = seq;
    if (ev.type === "redeal_vote_cast") {
      const vote = { name: ev.player_name, a: ev.votes_count, b: ev.total_count, next: ev.next_voter_name };
      feed(t(ev.next_voter_name ? "game.ev.voteCastNext" : "game.ev.voteCast", vote), "info");
      haptic.light();
    } else if (ev.type === "redeal_vote_continuer") {
      feed(t("game.ev.continuer", { name: ev.player_name }), "info");
    } else if (ev.type === "redeal_done") {
      feed(t("game.ev.redealDone"), "event");
      haptic.success(); sound.combo();
      setDealingKey((k) => k + 1);
      setIsDealing(true);
      setTimeout(() => setIsDealing(false), 1600);
    } else if (ev.type === "redeal_cancelled") {
      const names = ev.voter_names?.join(", ");
      if (names) {
        feed(t("game.ev.redealCancelled", { names }), "info");
      }
    }
  }, [state?.game?.last_event, state?.game?.event_seq]);

  const game = state?.game;
  // A completed set breaking, on every player's screen: the table keeps it in place until it has left.
  const { tableGame, run: brokenSetsRun } = useBrokenSets(game);
  // On a phone the groups also use the room above the scores (lib/tableRoom.js).
  const phoneBoard = usePhoneBoard();
  const tableRoom = useTableRoom({
    enabled: phoneBoard,
    ready: !!game,
    tableRef: tableScrollRef,
    listRef: tableListRef,
    columnRef: pileColumnRef,
  });
  const room = state?.room;

  // The redeal vote, for a player taking part in it: the board dims, the vote sits above the
  // hand, and the hand is lit so the player looks at the cards before choosing (owner's
  // request). It stays until somebody says « Continuer » (owner's request); the buttons only
  // on my turn. A chime when my turn comes, as for a turn (sound only).
  const inVote = game?.phase === "redeal_vote" && !!user?.user_id
    && !!game?.players?.some((p) => p.user_id === user.user_id && !p.eliminated);
  const myVoteTurn = inVote && game?.redeal_vote_current === user.user_id;
  const prevVoteTurnRef = useRef(false);
  useEffect(() => {
    if (myVoteTurn && !prevVoteTurnRef.current) sound.yourTurn();
    prevVoteTurnRef.current = myVoteTurn;
  }, [myVoteTurn]);
  // « Continuer » said by somebody else: the vote stays on screen with who chose and an OK, and
  // ends for me on that OK (owner's request); said by me, it ends at once. Only for a vote I took
  // part in (`voteRound`): a page opened later in the round shows nothing. The game goes on
  // meanwhile.
  const [voteRound, setVoteRound] = useState(null);
  const [voteOkRound, setVoteOkRound] = useState(null);
  useEffect(() => {
    if (inVote) setVoteRound(game.round_number);
  }, [inVote, game?.round_number]);
  const voteEndedBy = !inVote && voteRound != null && voteRound !== voteOkRound
    && game?.round_number === voteRound && (game.phase === "acting" || game.phase === "drawing")
    && game.redeal_continuer_id && game.redeal_continuer_id !== user?.user_id
    ? game.redeal_continuer_id : null;
  const showVote = inVote || !!voteEndedBy;

  // The vote, its bar and the hand go up to the middle of the table, and slide back down once the vote
  // ends (owner's request). How far: half the room left above them, never down (a phone held sideways
  // has none). The Dealer comes back once they have landed: its bubble is `fixed`, placed from where
  // its avatar is, and a moving ancestor (a transform) would carry it off.
  const matRef = useRef(null);
  const bottomRef = useRef(null);
  const voteSheetRef = useRef(null);
  const [voteLift, setVoteLift] = useState(0);
  const [feedHidden, setFeedHidden] = useState(false);
  const reduceMotion = useReducedMotion();
  useLayoutEffect(() => {
    if (!showVote) return undefined;
    const measure = () => {
      const mat = matRef.current, bottom = bottomRef.current, sheet = voteSheetRef.current;
      if (!mat || !bottom || !sheet) return;
      const below = parseFloat(getComputedStyle(bottom).paddingBottom) || 0; // the safe area: not part of the block
      const block = sheet.offsetHeight - VOTE_SHEET_OVERLAP + bottom.offsetHeight - below;
      setVoteLift(Math.max(0, Math.round((mat.clientHeight - below - block) / 2)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    [matRef, bottomRef, voteSheetRef].forEach((r) => r.current && ro.observe(r.current));
    return () => ro.disconnect();
  }, [showVote, loading]);
  useEffect(() => {
    if (showVote) {
      setFeedHidden(true);
      return undefined;
    }
    const timer = setTimeout(() => setFeedHidden(false), VOTE_SLIDE_S * 1000 + 50);
    return () => clearTimeout(timer);
  }, [showVote]);

  const me = useMemo(() => {
    if (!game) return null;
    return game.players.find((p) => p.user_id === user?.user_id) || null;
  }, [game, user]);

  // The end-of-game ad loads as soon as the game is over for this player (its end, or their
  // elimination), so the tap that leaves shows it at once. It never plays by itself (lib/ads.js).
  const gameOverForMe = game?.phase === "game_over" || !!me?.eliminated;
  useEffect(() => {
    if (gameOverForMe) preloadEndOfGameAd();
  }, [gameOverForMe]);

  // The discard pile cannot be taken on a player's first turn of the round,
  // nor when its top card is the previous player's first discard, nor while it
  // is locked (cards left by an eliminated player) — and not when it is empty.
  // The Dealer's prompts then only speak of the draw pile.
  const canTakeDiscard =
    !!game?.discard_top &&
    (me?.turns_played ?? 1) > 0 &&
    !game?.discard_locked &&
    !game?.first_discard_locked;

  // On a player's first turn of the round nothing can be posed (the backend
  // refuses before the 2nd turn and the pose bar is hidden): the only move
  // left after drawing is to discard, and the Dealer says just that.
  const canPose = (me?.turns_played ?? 1) > 0;

  const myHandRaw = game?.my_hand || [];

  // Sort card IDs for a pending group so the display matches the expected order.
  // Falls back to original order if any card is not found (safety guard).
  const sortGroupIds = (kind, ids) => {
    const pool = takenDiscardCard ? [...myHandRaw, takenDiscardCard] : myHandRaw;
    const cards = ids.map((id) => pool.find((c) => c.id === id)).filter(Boolean);
    if (cards.length !== ids.length) return ids;
    return sortGroupCards(kind, cards).map((c) => c.id);
  };

  // Cards currently locked into pending groups OR pending additions — hidden
  // from the hand; they return automatically when user removes them or cancels.
  const pendingCardIds = useMemo(
    () => new Set([
      ...pendingGroups.flatMap((g) => g.card_ids),
      ...pendingAdditions.flatMap((a) => a.card_ids),
      ...pendingReplacements.flatMap((r) =>
        r.additional_card_id
          ? [r.replacement_card_id, r.additional_card_id]
          : [r.replacement_card_id]
      ),
    ]),
    [pendingGroups, pendingAdditions, pendingReplacements]
  );

  // Apply sort (or manual drag-reorder). Include taken discard card when present.
  const myHand = useMemo(() => {
    let available = myHandRaw.filter((c) => !pendingCardIds.has(c.id));
    // Re-inject the staged Fausse card ONLY if it isn't already used in a
    // pendingGroup / pendingAddition. Without this guard the card visually
    // "stays" in the hand after being dragged into a group (regression bug
    // signalé : carte de la Fausse incluse dans une suite reste affichée
    // dans la main alors que les autres cartes du groupe partent).
    if (
      takenDiscardCard
      && !pendingCardIds.has(takenDiscardCard.id)
      && !available.some((c) => c.id === takenDiscardCard.id)
    ) {
      available = [...available, takenDiscardCard];
    }
    if (sortMode === "rank") return sortByValue(available);
    if (sortMode === "suit") return sortBySuit(available);
    if (manualOrder.length === 0) return available;
    const byId = Object.fromEntries(available.map((c) => [c.id, c]));
    const ordered = manualOrder.map((id) => byId[id]).filter(Boolean);
    const newCards = available.filter((c) => !manualOrder.includes(c.id));
    return [...ordered, ...newCards];
  }, [myHandRaw, pendingCardIds, sortMode, manualOrder, takenDiscardCard]);

  // The selection only holds cards of the hand on screen. A selected card that
  // leaves it (a recovered Joker whose swap is undone, a new deal…) leaves the
  // selection too: dropped along with another card, its id made the server
  // answer "Carte d'ajout absente de la main". Only the cards that left are
  // taken out — never "whatever this render's hand lacks": an effect can run
  // late, after a click that brought a card in and selected it (the discard
  // pile's card, just taken), and must not undo that selection.
  const shownHandIdsRef = useRef([]);
  useEffect(() => {
    const shown = new Set(myHand.map((c) => c.id));
    const left = shownHandIdsRef.current.filter((id) => !shown.has(id));
    shownHandIdsRef.current = [...shown];
    if (left.length === 0) return;
    setSelected((prev) => {
      if (!left.some((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      left.forEach((id) => next.delete(id));
      return next;
    });
  }, [myHand]);

  // A tap outside the hand's cards drops the selection (owner's request), the
  // hand's row beside them included. Only a tap that starts there too: a drag
  // from the hand ends with a click on whatever it was dropped over, and a scroll
  // of the table makes no click. Buttons (the cards are buttons) and the piles
  // keep their own action (taking the discard pile's card selects it).
  useEffect(() => {
    const KEEP = 'button, a, input, textarea, select, [role="button"], [role="dialog"], [role="menu"], [data-keep-selection]';
    const outside = (target) => !(target instanceof Element) || !target.closest(KEEP);
    let downOutside = false;
    const onDown = (e) => { downOutside = outside(e.target); };
    const onClick = (e) => {
      if (!downOutside || !outside(e.target)) return;
      setSelected((prev) => (prev.size ? new Set() : prev));
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick);
    };
  }, []);

  const isMyTurn =
    game?.current_player_id === user?.user_id &&
    !me?.eliminated &&
    (game?.phase === "drawing" || game?.phase === "acting");

  // Sync current hand display order to the backend so the round recap can show
  // each player's last arrangement. Debounced 400ms — fires on drag-reorder and
  // sort-mode change without spamming on rapid interactions.
  const handOrderSyncTimer = useRef(null);
  useEffect(() => {
    if (!roomId || !game || game.phase === "round_over" || game.phase === "game_over") return;
    const ids = myHand.map((c) => c.id);
    if (ids.length === 0) return;
    clearTimeout(handOrderSyncTimer.current);
    handOrderSyncTimer.current = setTimeout(() => {
      api.post(`/rooms/${roomId}/hand-order`, { order: ids }).catch(() => {});
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myHand]);

  // Tracks whether the player started Nemna3 this turn (staged a group or an
  // addition). Survives individual group removals (X button) so we can apply
  // the penalty even if pendingGroups is empty at discard time.
  const hasStartedNemna3Ref = useRef(false);

  // The « Nemna3 annulé » penalty is taken once a turn at most.
  const cancelPenaltyPaidRef = useRef(false);

  // The discard pile's card taken, then given back (« Annuler », dropped back on
  // the pile, or a pose with it refused): the penalty is owed even if the player
  // then poses or wins the round with a Nefrech (owner's rule, October 2026: a
  // take given back, a draw from the pile and a Nefrech went unpunished). The
  // server never knew of the take (it is staged here), so it is charged at once.
  const chargeGivenBackTake = async () => {
    if (cancelPenaltyPaidRef.current || me?.has_played_nemna3) return;
    cancelPenaltyPaidRef.current = true;
    hasStartedNemna3Ref.current = false;
    try {
      await api.post(`/rooms/${roomId}/nemna3/cancel`);
      const standardPenalty = state?.room?.standard_penalty ?? game?.standard_penalty ?? 50;
      if (standardPenalty > 0) feed(t("game.ok.nemna3CancelledPenalty", { n: standardPenalty }), "penalty");
    } catch {
      // Not charged: the discard that ends the turn takes it, if no pose comes.
      cancelPenaltyPaidRef.current = false;
      hasStartedNemna3Ref.current = true;
    }
  };

  // The server charged the penalty itself, for a discard it refused (`penalty` in
  // its answer: a first pose refused by a rule, the discard pile's card left out
  // of it). It is owed even if a pose or a Nefrech follows (owner's rule): none
  // is asked again this turn. Its log line says why and how much: the Dealer
  // shows that one, nothing is added here.
  const noteServerPenalty = () => {
    cancelPenaltyPaidRef.current = true;
    hasStartedNemna3Ref.current = false;
  };

  // A discard refused while the discard pile's card was taken (a pose under the
  // threshold…): the server gives the card back and the player draws again, so
  // the pose is cleared and the Jokers taken back for it go back to their groups.
  // That take is owed: the server charged it (a pose refused by a rule, the card
  // left out of it), or it is charged here (a pose refused for another reason).
  const afterRefusedTake = async (takenId, groups, additions, replacements, serverPenalty = 0) => {
    const takenInPose = groups.some((g) => g.card_ids.includes(takenId))
      || additions.some((a) => a.card_ids.includes(takenId))
      || replacements.some((r) => r.replacement_card_id === takenId || r.additional_card_id === takenId);
    hasStartedNemna3Ref.current = false;
    if (me?.has_played_nemna3) return;
    await undoWaitingJokerSwaps();
    if (serverPenalty > 0) {
      noteServerPenalty();
    } else if (takenInPose) {
      await chargeGivenBackTake();
    } else {
      cancelPenaltyPaidRef.current = true;
    }
  };
  useEffect(() => {
    if (pendingGroups.length > 0 || pendingAdditions.length > 0) {
      hasStartedNemna3Ref.current = true;
    }
  }, [pendingGroups, pendingAdditions]);

  // Reset local pending state when the turn passes to another player.
  useEffect(() => {
    if (!isMyTurn) {
      setPendingGroups([]);
      setPendingAdditions([]);
      setPendingReplacements([]);
      setRecoveredJokers([]);
      setCommittedAdditions([]);
      setSelected(new Set());
      setTakenDiscardCard(null);
      hasStartedNemna3Ref.current = false;
      cancelPenaltyPaidRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTurn]);

  // Defensive: when the server confirms our combos count went UP, clear any
  // remaining local staging (prevents the rare visual duplication where the
  // pending preview block lingers after a successful commit).
  const lastCombosCountRef = useRef(0);
  useEffect(() => {
    const count = me?.combinations?.length || 0;
    if (count > lastCombosCountRef.current) {
      setPendingGroups([]);
      setPendingAdditions([]);
      setPendingReplacements([]);
    }
    lastCombosCountRef.current = count;
  }, [me?.combinations?.length]);

  // ── The turn timer (lib/turnClock.js; the server runs it) ────────────────
  // Rings round the avatars (TurnRing); on my own clock, the Dealer warns at 10 s and a soft tick
  // marks each of the last 5. When it runs out the server plays for me, and the turn passing clears
  // the pose I was composing (the effect above).
  const turnClock = useTurnClock(game?.turn_timer);
  const myClock = turnClock?.userId === user?.user_id ? turnClock : null;
  useEffect(() => {
    if (!myClock) return undefined;
    const left = msLeft(myClock);
    const timers = [];
    if (left > 10000) {
      timers.push(setTimeout(() => {
        if (myClock.kind === "turn") feed(t("game.timer.hurry"), "event", { transient: true });
        sound.timeHurry();
      }, left - 10000));
    }
    for (let s = 5; s >= 1; s--) {
      if (left > s * 1000) timers.push(setTimeout(() => sound.clockTick(), left - s * 1000));
    }
    return () => timers.forEach(clearTimeout);
  }, [myClock, feed]);
  // ─────────────────────────────────────────────────────────────────────────

  // ── Tour start — notification instantanée via feed ───────────────────────
  const prevIsMyTurnRef = useRef(false);
  useEffect(() => {
    const wasMyTurn = prevIsMyTurnRef.current;
    prevIsMyTurnRef.current = isMyTurn;
    if (!isMyTurn || wasMyTurn) return; // only fire on transition → my turn
    if (game?.phase === "drawing") {
      feed(canTakeDiscard ? t("game.turn.draw") : t("game.turn.drawOnly"), "info");
    } else if (game?.phase === "acting") {
      feed(canPose ? t("game.turn.act") : t("game.turn.discardOnly"), "info");
    }
  }, [isMyTurn, game?.phase]); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────

  // ── Idle nudge — the Dealer teases a player who lets the turn sit ────────
  // Any interaction (selecting, reordering, staging a group, drawing) restarts
  // the clock. A language with empty `game.nudge.*` lists gets no nudge.
  // The lines already said, per list: a line comes back only once the others have been said.
  const saidNudgesRef = useRef({});
  const waitingToDraw = game?.phase === "drawing" && !takenDiscardCard;
  useEffect(() => {
    if (!isMyTurn) return;
    let sent = 0;
    let timer;
    const schedule = (delay) => {
      timer = setTimeout(() => {
        const key = !waitingToDraw
          ? canPose ? "game.nudge.act" : "game.nudge.discardOnly"
          : canTakeDiscard ? "game.nudge.draw" : "game.nudge.drawOnly";
        const lines = tList(key);
        if (lines.length === 0) return;
        if (document.visibilityState === "visible") {
          let said = saidNudgesRef.current[key] || [];
          let fresh = lines.filter((l) => !said.includes(l));
          if (fresh.length === 0) {
            said = [];
            fresh = lines;
          }
          const line = fresh[Math.floor(Math.random() * fresh.length)];
          saidNudgesRef.current[key] = [...said, line];
          feed(line, "info", { transient: true }); // a nudge: the bubble only, not the journal
          haptic.light();
        }
        sent += 1;
        if (sent < NUDGE_MAX) schedule(NUDGE_REPEAT_MS);
      }, delay);
    };
    schedule(NUDGE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [isMyTurn, waitingToDraw, canTakeDiscard, canPose, pendingGroups.length, pendingAdditions.length, selected, manualOrder, feed]);

  // The others from my left in the order of play (clockwise, lib/seating.js): the row shows them left to
  // right, so the turn goes round the table one way.
  const opponents = useMemo(() => {
    if (!game) return [];
    return seatsFrom(game.players, user?.user_id).filter(Boolean);
  }, [game, user]);

  const playerColorMap = useMemo(() => {
    if (!game?.players?.length) return {};
    return buildPlayerColorMap(game.players, roomId);
  }, [game?.players?.map((p) => p.user_id)?.join(","), roomId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const myPosedPoints = useMemo(() => {
    if (!me) return 0;
    return (me.combinations || []).reduce((sum, c) => sum + (c.points || 0), 0);
  }, [me]);

  // Total points in pending groups (sum from validated groups)
  const totalPendingPoints = useMemo(
    () => pendingGroups.reduce((s, g) => s + (g.points || 0), 0),
    [pendingGroups]
  );

  const pendingHasPureSeq = useMemo(
    () => pendingGroups.some(
      g => g.kind === "sequence" &&
      g.card_ids.every(cid => !myHandRaw.find(c => c.id === cid)?.is_joker)
    ),
    [pendingGroups, myHandRaw]
  );

  const thresholdMet = useMemo(() => {
    if (!game) return false;
    const pureSeqReq = !game.require_pure_sequence || pendingHasPureSeq;
    return pureSeqReq && totalPendingPoints >= (game.current_threshold || 0);
  }, [game, pendingHasPureSeq, totalPendingPoints]);

  const toggleSelect = (cid) => {
    // Allow selection even when it's not your turn (so users can plan/sort)
    const s = new Set(selected);
    if (s.has(cid)) s.delete(cid);
    else s.add(cid);
    setSelected(s);
  };

  const resetSelection = () => {
    setSelected(new Set());
    setPendingGroups([]);
    setPendingAdditions([]);
    setPendingReplacements([]);
  };

  // Group building: validate with backend before accepting.
  // `kind` peut être "sequence", "set" ou "auto" (essaie les deux).
  const addGroup = async (kind) => {
    if (selected.size < 3) {
      errToast(t("game.err.select3"));
      return;
    }
    const cardIds = [...selected];
    const kindsToTry = kind === "auto" ? ["sequence", "set"] : [kind];
    let lastError = t("game.err.invalidCombo");
    let validated = null;
    for (const k of kindsToTry) {
      try {
        const { data } = await api.post(`/rooms/${roomId}/validate-combination`, {
          kind: k,
          card_ids: cardIds,
          include_discard_top: !!takenDiscardCard,
        });
        if (data.valid) {
          validated = { kind: k, points: data.points };
          break;
        }
        if (data.error) lastError = data.error;
      } catch (e) {
        lastError = e?.response?.data?.detail || t("game.err.validation");
      }
    }
    if (!validated) {
      errToast(lastError);
      return;
    }
    const alreadyStaged =
      pendingGroups.reduce((s, g) => s + g.card_ids.length, 0) +
      pendingAdditions.reduce((s, a) => s + a.card_ids.length, 0) +
      pendingReplacements.reduce((s, r) => s + 1 + (r.additional_card_id ? 1 : 0), 0);
    if (alreadyStaged + cardIds.length > 14) {
      errToast(t("game.err.max14Cancelled", { n: alreadyStaged + cardIds.length }));
      return;
    }
    // Must keep at least 1 card to discard (0 left = nothing to discard)
    const afterStagedIds = new Set([
      ...pendingGroups.flatMap(g => g.card_ids),
      ...pendingAdditions.flatMap(a => a.card_ids),
      ...cardIds,
    ]);
    const remainingAfter = myHandRaw.filter(c => !afterStagedIds.has(c.id));
    if (remainingAfter.length < 1) {
      errToast(t("game.err.keepOne"));
      return;
    }
    setPendingGroups([
      ...pendingGroups,
      { kind: validated.kind, card_ids: sortGroupIds(validated.kind, cardIds), points: validated.points },
    ]);
    setSelected(new Set());
  };

  const removeGroup = (idx) => {
    setPendingGroups((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setPendingAdditions([]);
      return next;
    });
  };

  // === Actions ===
  // Single-flight guard: prevents double-clicks from racing two requests.
  // Stays locked until the server's WS broadcast catches up (state.version
  // bumps), so the local `game.phase` reflects the server before re-allowing.
  const pendingActionRef = useRef(false);
  const guardedAction = useCallback(async (fn) => {
    if (pendingActionRef.current) return;
    pendingActionRef.current = true;
    const startVersion = lastVersionRef.current;
    try {
      const result = await fn();
      // Wait up to ~1.5s for the WS state update with a higher version
      const t0 = Date.now();
      while (lastVersionRef.current === startVersion && Date.now() - t0 < 1500) {
        await new Promise((r) => setTimeout(r, 30));
      }
      return result;
    } finally {
      pendingActionRef.current = false;
    }
  }, []);

  const handleDrawPile = async () => {
    await guardedAction(async () => {
      try {
        await api.post(`/rooms/${roomId}/draw/pile`);
      } catch (e) {
        errToast(e?.response?.data?.detail || t("common.error"));
      }
    });
  };

  // Take top of discard pile (Click directly on Fausse).
  // - If has_played_nemna3 already: server takes freely (no combo required).
  // - Else: client stages it into the hand visually; player composes combos
  //   that include it; the take is committed atomically at end-turn discard
  //   via /discard with `take_discard_first=true`.
  const handleTakeDiscard = async () => {
    if (!game?.discard_top) { errToast(t("game.err.fausseEmpty")); return; }
    if (!isMyTurn) { errToast(t("game.err.notYourTurn")); return; }
    if (game.phase !== "drawing") { errToast(t("game.err.alreadyDrewTurn")); return; }
    if (game.discard_locked) {
      errToast(t("game.err.discardLocked"));
      haptic.error();
      return;
    }
    if (!canTakeDiscard) {
      errToast(t("game.err.discardClosed"));
      haptic.error();
      return;
    }
    if (me?.has_played_nemna3) {
      triggerFly("defausse-pile", "player-hand", null, "draw");
      await guardedAction(async () => {
        try {
          const { data } = await api.post(`/rooms/${roomId}/draw/discard`, { combinations: [] });
          if (data.success) feed(data.message || t("game.ok.cardTaken"), "success");
          else errToast(data.message);
        } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
      });
      return;
    }
    triggerFly("defausse-pile", "player-hand", null, "draw");
    // Stage the discard card into hand visually; user composes pendingGroups
    // with it; commit happens at end-turn discard via take_discard_first=true.
    const taken = game.discard_top;
    setTakenDiscardCard(taken);
    // Auto-select the taken card so the player sees it highlighted immediately.
    setSelected(new Set([taken.id]));
    // Insert at middle of current hand — same behaviour as drawing from the pile.
    const currentIds = myHand.map((c) => c.id);
    const mid = Math.ceil(currentIds.length / 2);
    setManualOrder([...currentIds.slice(0, mid), taken.id, ...currentIds.slice(mid)]);
    setSortMode("manual");
    haptic.medium();
    feed(t("game.ok.stagedTaken"), "info");
  };

  // Cancel ALL pending actions in flight + apply standard penalty.
  // Affichage : la condition de visibilité du bouton est dans <PiocheArea/>
  // — cancel disponible UNIQUEMENT si une action Nemna3 est entamée
  const handleVoteRedeal = async (choice) => {
    if (voting) return;
    setVoting(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/vote-redeal`, { choice });
      if (!data.success) errToast(data.message);
    } catch (e) {
      errToast(e?.response?.data?.detail || t("common.error"));
    } finally {
      setVoting(false);
    }
  };

  const handleBackToLobby = async () => {
    setBackingToLobby(true);
    // The end-of-game ad, after this tap only when the game is over for this player (its end
    // screens, or the exit button once eliminated): never when they leave a game under way.
    if (gameOverForMe) await showEndOfGameAd(game?.game_id, Date.now() - openedAtRef.current);
    try {
      await api.post(`/rooms/${roomId}/quit`);
    } catch {
      // ignore — navigate anyway
    }
    navigate("/lobby");
  };

  // Replay with the same players and settings: the server answers with the
  // shared waiting room, or with a game already started when I was the only
  // human of this one.
  const handleRematch = async () => {
    setRematching(true);
    // The only human of this game (as the server sees it): nobody waits in the next room, so the
    // end-of-game ad may play before it. With other humans, none: the group is not kept waiting.
    if ((game?.players || []).filter((p) => !p.is_bot).length === 1) {
      await showEndOfGameAd(game?.game_id, Date.now() - openedAtRef.current);
    }
    try {
      const { data } = await api.post(`/rooms/${roomId}/rematch`);
      navigate(`/${data.room.started ? "game" : "room"}/${data.room.room_id}`);
    } catch (e) {
      setRematching(false);
      toast.error(trServer(e?.response?.data?.detail) || t("common.error"));
    }
  };

  // Players of this game already sitting in the rematch room.
  const rematchNames = (state?.rematch?.waiting || [])
    .filter((w) => w.user_id !== user?.user_id)
    .map((w) => w.name);
  const rematchHint = rematchNames.length
    ? t("common.rematchWaiting", { names: rematchNames.join(", "), n: rematchNames.length })
    : "";

  // (pendingGroups/pendingAdditions) ou si une carte a été prise de la
  // défausse (takenDiscardCard). Une simple pioche depuis la Pioche n'ouvre
  // PAS l'annulation : il n'y a rien à annuler tant que rien n'est posé.
  //
  // Before a first pose, a Joker swap waits for that pose: undo them (no cost).
  const undoWaitingJokerSwaps = async () => {
    const swapped = new Map(recoveredJokers.map((r) => [r.combination_id, r.target_user_id]));
    for (const [comboId, ownerId] of swapped) {
      try {
        const { data } = await api.post(`/rooms/${roomId}/undo-all-combo-mods`, {
          target_user_id: ownerId,
          combination_id: comboId,
        });
        if (!data.success) { errToast(data.message); return false; }
      } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); return false; }
    }
    setRecoveredJokers([]);
    return true;
  };

  // "Annuler" puts the turn back as it began, as removing every group with its
  // × does: the cards go back to the hand, the discard pile's card to the pile,
  // and the Jokers taken back for a first pose to their groups (no cost). The
  // penalty for a pose given up is not taken here: the discard takes it
  // (`nemna3/cancel`) if the turn ends without a pose. Except when the discard
  // pile's card goes back: that one is owed whatever comes next (chargeGivenBackTake).
  const handleCancelStaging = async () => {
    const hasStartedNemna3 = pendingGroups.length > 0 || pendingAdditions.length > 0;
    const hasStagedAnything = hasStartedNemna3 || pendingReplacements.length > 0 || !!takenDiscardCard;
    if (!hasStagedAnything) return;

    if (!me?.has_played_nemna3 && !(await undoWaitingJokerSwaps())) return;
    resetSelection();
    setTakenDiscardCard(null);
    feed(t("game.ok.cancelled"), "info");
    if (takenDiscardCard) await chargeGivenBackTake();
    else if (hasStartedNemna3) hasStartedNemna3Ref.current = true;
  };

  const handleUndoJoker = async (recovery) => {
    try {
      const { data } = await api.post(`/rooms/${roomId}/undo-joker`, {
        target_user_id: recovery.target_user_id,
        combination_id: recovery.combination_id,
        joker_card_id: recovery.joker_card_id,
        replacement_card_id: recovery.replacement_card_id,
        additional_card_id: recovery.additional_card_id || null,
      });
      if (data.success) {
        setRecoveredJokers((prev) => prev.filter((r) => r.joker_card_id !== recovery.joker_card_id));
        feed(t("game.ok.jokerUndo"), "success");
        haptic.light();
      } else {
        errToast(data.message);
      }
    } catch (e) {
      errToast(e?.response?.data?.detail || t("common.error"));
    }
  };

  const _trackCommittedAddition = (targetUserId, combinationId, cardIds) => {
    setCommittedAdditions((prev) => {
      const idx = prev.findIndex(
        (a) => a.combination_id === combinationId && a.target_user_id === targetUserId
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], card_ids: [...next[idx].card_ids, ...cardIds] };
        return next;
      }
      return [...prev, { target_user_id: targetUserId, combination_id: combinationId, card_ids: cardIds }];
    });
  };

  // Undo ALL modifications on a combo this turn via a single backend call.
  // The backend restores the pre-turn snapshot atomically (handles additions,
  // Joker re-placements, and Joker recovery in any order).
  const handleUndoAllComboChanges = async (comboId, targetUserId) => {
    // A Joker taken back from this group and put in a group being composed
    // would go back to the table while that group still counts on it: the
    // discard would then fail ("Carte absente de la main").
    const jokerInPendingGroup = recoveredJokers.some((r) => r.combination_id === comboId
      && pendingGroups.some((g) => g.card_ids.includes(r.joker_card_id)));
    if (jokerInPendingGroup) {
      errToast(t("game.err.jokerInPendingGroup"));
      return;
    }
    try {
      const { data } = await api.post(`/rooms/${roomId}/undo-all-combo-mods`, {
        target_user_id: targetUserId,
        combination_id: comboId,
      });
      if (!data.success) { errToast(data.message); return; }

      // Clear client-side tracking for this combo
      setCommittedAdditions((prev) =>
        prev.filter((a) => !(a.combination_id === comboId && a.target_user_id === targetUserId))
      );
      setRecoveredJokers((prev) =>
        prev.filter((r) => !(r.combination_id === comboId))
      );

      feed(t("game.ok.modsUndone"), "success");
      haptic.light();
    } catch (e) {
      errToast(e?.response?.data?.detail || t("common.error"));
    }
  };

  // Apply the penalty if user closes/refreshes the page while having a pending
  // pose — or a first pose given up this turn ("Annuler", ×): the discard that
  // would take that penalty never comes.
  useEffect(() => {
    const onBeforeUnload = () => {
      const token = getSessionToken();
      // Use fetch with keepalive (sendBeacon doesn't support custom headers)
      const post = (path, body) => {
        try {
          fetch(`${api.defaults.baseURL}/rooms/${roomId}/${path}`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : null) },
            body: body ? JSON.stringify(body) : undefined,
            keepalive: true,
          });
        } catch {}
      };
      // Charged already this turn: only the Jokers taken back for a first pose
      // go back to their groups, as the cancel would have done.
      if (cancelPenaltyPaidRef.current) {
        if (me?.has_played_nemna3) return;
        new Map(recoveredJokers.map((r) => [r.combination_id, r.target_user_id])).forEach((ownerId, comboId) =>
          post("undo-all-combo-mods", { target_user_id: ownerId, combination_id: comboId }));
        return;
      }
      const hasPending = pendingGroups.length > 0
        || pendingAdditions.length > 0
        || pendingReplacements.length > 0
        || (hasStartedNemna3Ref.current && !me?.has_played_nemna3);
      if (!hasPending) return;
      post("nemna3/cancel");
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [pendingGroups, pendingAdditions, pendingReplacements.length, recoveredJokers, me?.has_played_nemna3, roomId]);

  const handleNextRound = async () => {
    try {
      await api.post(`/rooms/${roomId}/next-round`);
    } catch (e) {
      errToast(e?.response?.data?.detail || t("common.error"));
      refetchState(); // refused: the role has moved, or the round has already started
    }
  };

  // Broadcast pending-groups + pending-additions + took_discard preview to other players in real-time
  useEffect(() => {
    if (!user) return;
    const pool = takenDiscardCard ? [...myHandRaw, takenDiscardCard] : myHandRaw;
    const cardById = Object.fromEntries(pool.map((c) => [c.id, c]));
    const hasPending = pendingGroups.length > 0 || pendingAdditions.length > 0;
    const payload = {
      mode: hasPending ? "compose" : null,
      groups: pendingGroups.map((g) => ({
        kind: g.kind,
        cards: g.card_ids.map((cid) => cardById[cid]).filter(Boolean),
      })),
      additions: pendingAdditions.map((a) => ({
        target_user_id: a.target_user_id,
        combination_id: a.combination_id,
        cards: a.card_ids.map((cid) => cardById[cid]).filter(Boolean),
      })),
      took_discard: !!takenDiscardCard,
    };
    const timer = setTimeout(() => {
      api.post(`/rooms/${roomId}/preview`, payload).catch(() => {});
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGroups, pendingAdditions, takenDiscardCard, roomId, user?.user_id]);

  // Gives a Joker of a group on the table back to the hand, at once, for real
  // card(s) of the hand (2 for a 3-card set). While a first pose is composed,
  // the server only checks its groups (with the discard pile's card when the
  // turn began by taking it): they stay in preparation — × and points counter
  // — until the discard posts them, and the swap is provisional until then
  // (cancelling the pose gives the Joker back).
  const swapJoker = ({ ownerId, comboId, jokerId, replacementId, additionalId = null }) =>
    guardedAction(async () => {
      const posing = !me?.has_played_nemna3;
      try {
        const { data } = await api.post(`/rooms/${roomId}/replace-joker`, {
          target_user_id: ownerId,
          combination_id: comboId,
          joker_card_id: jokerId,
          replacement_card_id: replacementId,
          additional_card_id: additionalId,
          staged_combinations: posing
            ? pendingGroups.map((g) => ({ kind: g.kind, card_ids: g.card_ids }))
            : null,
          include_discard_top: posing && !!takenDiscardCard,
        });
        if (!data.success) { errToast(data.message); return; }
        feed(t("game.ok.jokerRecovered"), "success");
        haptic.success(); sound.combo();
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(replacementId);
          if (additionalId) next.delete(additionalId);
          return next;
        });
        setRecoveredJokers((prev) => [...prev, {
          target_user_id: ownerId,
          combination_id: comboId,
          joker_card_id: jokerId,
          replacement_card_id: replacementId,
          additional_card_id: additionalId,
        }]);
      } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
    });

  // === Drag & Drop handlers ===
  // Shared discard logic — called both from DnD drop and from click-to-discard
  const handleDragStart = (event) => {
    const cardId = event.active.id;
    const card = myHandRaw.find((c) => c.id === cardId)
      ?? (takenDiscardCard?.id === cardId ? takenDiscardCard : null);
    setActiveDrag(card);
  };

  const handleDragOver = (event) => {
    const data = event.over?.data?.current;
    setActiveDropComboId(data?.type === "combo" ? (data.combo_id ?? null) : null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    setActiveDropComboId(null);
    if (!over) return;
    const cardId = active.id;

    // Case 1: Dropped on another hand card → reorder
    const isHandReorder = myHand.some((c) => c.id === over.id);
    if (isHandReorder && cardId !== over.id) {
      const currentIds = myHand.map((c) => c.id);
      const draggedIdx = currentIds.indexOf(cardId);
      const targetIdx = currentIds.indexOf(over.id);
      if (draggedIdx === -1 || targetIdx === -1) return;

      const isGroupDrag = selected.has(cardId) && selected.size > 1;
      if (isGroupDrag) {
        // Déplace toutes les cartes sélectionnées comme un bloc, en conservant leur ordre relatif
        const selectedInOrder = currentIds.filter((id) => selected.has(id));
        const withoutSelected = currentIds.filter((id) => !selected.has(id));
        const overInWithout = withoutSelected.indexOf(over.id);
        let insertIndex;
        if (overInWithout !== -1) {
          // La cible n'est pas dans le groupe : insérer avant ou après selon la direction
          insertIndex = targetIdx > draggedIdx ? overInWithout + 1 : overInWithout;
        } else {
          // La cible est elle-même sélectionnée : compter les non-sélectionnés avant elle
          insertIndex = currentIds
            .slice(0, targetIdx + (targetIdx > draggedIdx ? 1 : 0))
            .filter((id) => !selected.has(id)).length;
        }
        insertIndex = Math.max(0, Math.min(insertIndex, withoutSelected.length));
        const result = [...withoutSelected];
        result.splice(insertIndex, 0, ...selectedInOrder);
        const allIds = [...result, ...myHandRaw.map((c) => c.id).filter((id) => !result.includes(id))];
        setManualOrder(allIds);
        setSortMode("manual");
        setSelected(new Set());
        return;
      }

      // Carte unique : comportement d'origine
      const reordered = arrayMove(currentIds, draggedIdx, targetIdx);
      const allIds = [...reordered, ...myHandRaw.map((c) => c.id).filter((id) => !reordered.includes(id))];
      setManualOrder(allIds);
      setSortMode("manual");
      setSelected(new Set());
      return;
    }

    // Case 2: Dropped on a DropZone → perform action (trust backend for validation)
    const dropData = over.data?.current || {};
    // Only cards of the hand on screen, even before the selection catches up
    // with the last state from the server.
    const cardsToMove = selected.has(cardId) && selected.size > 1
      ? [...selected].filter((id) => myHand.some((c) => c.id === id))
      : [cardId];

    if (dropData.type === "discard") {
      if (cardsToMove.length > 1) { errToast(t("game.err.oneCardDiscard")); return; }
      if (game.phase === "drawing" && !takenDiscardCard) {
        errToast(t("game.err.drawFirst"));
        haptic.error();
        return;
      }

      // Cas : le joueur re-défausse la MÊME carte qu'il a prise de la défausse
      // sans avoir fait de Nemna3 → identique au clic "Annuler" : la carte
      // retourne sur la défausse, le tour continue (pénalité à la défausse
      // qui finira le tour, s'il n'a toujours pas posé).
      if (takenDiscardCard && cardId === takenDiscardCard.id && !me?.has_played_nemna3 && pendingGroups.length === 0) {
        triggerFly("player-hand", "defausse-pile", takenDiscardCard, "discard");
        await handleCancelStaging();
        return;
      }

      const stagedCardId = takenDiscardCard?.id;
      const groupsForServer = pendingGroups.map((g) => ({ kind: g.kind, card_ids: g.card_ids }));
      const validatingNemna3 = pendingGroups.length > 0;
      const commitAdditions = !!me?.has_played_nemna3 || validatingNemna3;
      if (!me?.has_played_nemna3 && !validatingNemna3 && cancelPenaltyPaidRef.current) {
        // Already charged this turn (a take given back): the Jokers taken back
        // for the pose given up still go back to their groups, free.
        hasStartedNemna3Ref.current = false;
        if (!commitAdditions && pendingAdditions.length > 0) setPendingAdditions([]);
        if (recoveredJokers.length > 0 && !(await undoWaitingJokerSwaps())) return;
      } else if (!me?.has_played_nemna3 && !validatingNemna3 && hasStartedNemna3Ref.current) {
        hasStartedNemna3Ref.current = false;
        cancelPenaltyPaidRef.current = true;
        if (!commitAdditions && pendingAdditions.length > 0) setPendingAdditions([]);
        setRecoveredJokers([]); // the cancel gives back the Jokers taken during the pose
        try {
          await api.post(`/rooms/${roomId}/nemna3/cancel`);
          const standardPenalty = state?.room?.standard_penalty ?? game?.standard_penalty ?? 50;
          if (standardPenalty > 0) feed(t("game.ok.nemna3CancelledPenalty", { n: standardPenalty }), "penalty");
        } catch {}
      } else if (!commitAdditions && pendingAdditions.length > 0) {
        setPendingAdditions([]);
      }
      const additionsForServer = commitAdditions
        ? pendingAdditions.map((a) => ({
            target_user_id: a.target_user_id,
            combination_id: a.combination_id,
            card_ids: a.card_ids,
            joker_position: a.joker_position || null,
          }))
        : [];
      const replacementsForServer = pendingReplacements.map((r) => ({
        target_user_id: r.target_user_id,
        combination_id: r.combination_id,
        joker_card_id: r.joker_card_id,
        replacement_card_id: r.replacement_card_id,
        additional_card_id: r.additional_card_id || null,
      }));
      // Flag to detect whether guardedAction actually executed vs. was blocked.
      let discardAttempted = false;
      let discardSucceeded = false;
      let serverPenalty = 0;
      await guardedAction(async () => {
        discardAttempted = true;
        try {
          const { data } = await api.post(`/rooms/${roomId}/discard`, {
            card_id: cardId,
            combinations: groupsForServer,
            additions: additionsForServer,
            replacements: replacementsForServer,
            take_discard_first: !!stagedCardId,
          });
          serverPenalty = data.penalty || 0;
          if (data.success) {
            discardSucceeded = true;
            setSelected(new Set());
            resetSelection();
            setTakenDiscardCard(null);
          } else if (serverPenalty > 0) {
            // Charged: the server's log line gives the reason and the points (the
            // Dealer's message). The bare reason here would only hide it.
            haptic.error(); sound.error();
          } else {
            errToast(data.message);
          }
        } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
      });
      // Si le discard a été tenté (guardedAction non bloqué) mais a échoué ET qu'une
      // carte de défausse était stagée → nettoyage du staging (backend a rollbacké la prise).
      if (discardAttempted && !discardSucceeded && stagedCardId) {
        setTakenDiscardCard(null);
        setPendingGroups([]);
        setPendingAdditions([]);
        setPendingReplacements([]);
        setRecoveredJokers([]);
        resetSelection();
        await afterRefusedTake(stagedCardId, groupsForServer, additionsForServer, replacementsForServer, serverPenalty);
      } else if (serverPenalty > 0) {
        // A first pose refused (no run without a Joker, threshold not reached):
        // it stays composed, for the player to fix, but the penalty is taken.
        noteServerPenalty();
      }
      return;
    }

    if (dropData.type === "pending-group") {
      // Drop on a pending (not-yet-committed) group — extend it with more cards.
      const groupIdx = dropData.groupIdx;
      const group = pendingGroups[groupIdx];
      if (!group) return;
      // Check max-14 before validating (new cards count against the total).
      const alreadyStaged =
        pendingGroups.reduce((s, g, i) => s + (i === groupIdx ? 0 : g.card_ids.length), 0) +
        pendingAdditions.reduce((s, a) => s + a.card_ids.length, 0) +
        pendingReplacements.reduce((s, r) => s + 1 + (r.additional_card_id ? 1 : 0), 0);
      if (alreadyStaged + group.card_ids.length + cardsToMove.length > 14) {
        errToast(t("game.err.max14", { n: alreadyStaged + group.card_ids.length + cardsToMove.length }));
        return;
      }
      // Prevent leaving hand completely empty (need at least 1 card for discard / Nefrech)
      const afterStagedIds = new Set([
        ...pendingGroups.flatMap((g, i) => i === groupIdx ? [...g.card_ids, ...cardsToMove] : g.card_ids),
        ...pendingAdditions.flatMap(a => a.card_ids),
      ]);
      if (myHandRaw.filter(c => !afterStagedIds.has(c.id)).length < 1) {
        errToast(t("game.err.keepOne"));
        return;
      }
      // Special case: single Joker on a pending sequence — determine valid
      // positions by rank range (client-side) to avoid the backend treating
      // both array orderings as equivalent.
      const isSingleJoker =
        cardsToMove.length === 1 &&
        myHandRaw.find((c) => c.id === cardsToMove[0])?.rank === "JOKER";
      if (isSingleJoker && group.kind === "sequence") {
        const jokerCardId = cardsToMove[0];
        const pool = takenDiscardCard ? [...myHandRaw, takenDiscardCard] : myHandRaw;
        const run = layoutSequence(group.card_ids.map((id) => pool.find((c) => c.id === id)).filter(Boolean));
        if (!run || run.length !== group.card_ids.length) {
          errToast(t("game.err.jokerPosUnknown"));
          return;
        }
        const groupSuit = run.find((c) => !c.is_joker)?.suit ?? null;
        // The rank the Joker would stand for at each end, null when that end is
        // closed: nothing before the Ace of A-2-3, nothing after the Ace of Q-K-A.
        const ends = sequenceJokerEnds(run);
        const leftValid  = !!ends.left;
        const rightValid = !!ends.right;
        if (!leftValid && !rightValid) {
          // An end that ends with a Joker is closed by the rule: two Jokers never side by side.
          const byJoker = run[0].is_joker || run[run.length - 1].is_joker;
          errToast(t(byJoker ? "game.err.jokersSideBySide" : "game.err.jokerNoSlot"));
          return;
        }
        const runIds   = run.map((c) => c.id);
        const leftIds  = [jokerCardId, ...runIds];
        const rightIds = [...runIds, jokerCardId];
        try {
          const candidateIds = rightValid ? rightIds : leftIds;
          const { data } = await api.post(`/rooms/${roomId}/validate-combination`, {
            kind: "sequence", card_ids: candidateIds,
            include_discard_top: !!takenDiscardCard,
          });
          if (!data.valid) {
            errToast(data.error || t("game.err.jokerCantAdd"));
            return;
          }
          if (leftValid && rightValid) {
            const leftCard  = groupSuit ? { rank: ends.left,  suit: groupSuit } : null;
            const rightCard = groupSuit ? { rank: ends.right, suit: groupSuit } : null;
            setJokerInsertChoice({
              pendingGroupIdx: groupIdx,
              pendingCardIdsLeft: leftIds,
              pendingCardIdsRight: rightIds,
              pendingPointsLeft: data.points,
              pendingPointsRight: data.points,
              leftCard,
              rightCard,
            });
            setSelected(new Set());
            return;
          }
          // Kept as built, like the dialog's choice (handleJokerInsertChoice).
          const chosenIds = leftValid ? leftIds : rightIds;
          setPendingGroups((prev) => prev.map((g, i) =>
            i === groupIdx ? { ...g, card_ids: chosenIds, points: data.points } : g
          ));
          setSelected(new Set());
          feed(t(leftValid ? "game.ok.jokerLeftAuto" : "game.ok.jokerRightAuto"), "success");
          haptic.light();
        } catch (e) {
          errToast(e?.response?.data?.detail || t("game.err.validation"));
        }
        return;
      }
      // Regular cards (or Joker on a set).
      // For sequences: if the group already has a Joker, first try replacing it
      // with the real card (Joker returns to hand). Only fall back to extending
      // if the replacement doesn't yield a valid sequence.
      const groupHasJoker =
        group.kind === "sequence" &&
        group.card_ids.some(id => myHandRaw.find(c => c.id === id)?.rank === "JOKER");

      if (groupHasJoker) {
        const nonJokerGroupIds = group.card_ids.filter(
          id => myHandRaw.find(c => c.id === id)?.rank !== "JOKER"
        );
        const withoutJokerIds = [...nonJokerGroupIds, ...cardsToMove];
        try {
          const { data: resNoJoker } = await api.post(`/rooms/${roomId}/validate-combination`, {
            kind: "sequence", card_ids: withoutJokerIds,
            include_discard_top: !!takenDiscardCard,
          });
          if (resNoJoker.valid) {
            // Real card fills the Joker's spot — Joker goes back to hand.
            setPendingGroups((prev) => prev.map((g, i) =>
              i === groupIdx ? { ...g, card_ids: sortGroupIds("sequence", withoutJokerIds), points: resNoJoker.points } : g
            ));
            setSelected(new Set());
            feed(t("game.ok.jokerRecoveredReal"), "success");
            haptic.light();
            return;
          }
        } catch { /* fall through to normal extension */ }
      }

      // Normal extension: append and validate.
      const allCardIds = [...group.card_ids, ...cardsToMove];
      try {
        const { data } = await api.post(`/rooms/${roomId}/validate-combination`, {
          kind: group.kind,
          card_ids: allCardIds,
          include_discard_top: !!takenDiscardCard,
        });
        if (!data.valid) {
          errToast(data.error || t("game.err.invalidWithCards"));
          return;
        }
        setPendingGroups((prev) => prev.map((g, i) =>
          i === groupIdx
            ? { ...g, card_ids: sortGroupIds(group.kind, allCardIds), points: data.points }
            : g
        ));
        setSelected(new Set());
        feed(t("game.ok.addedToGroup", { n: cardsToMove.length }), "success");
        haptic.light();
      } catch (e) {
        errToast(e?.response?.data?.detail || t("game.err.validation"));
      }
      return;
    }

    if (dropData.type === "combo") {
      // A drop anywhere on a group of the table: the Joker is not a zone of its
      // own. A real card that takes a Joker's place gives the Joker back;
      // anything else is added to the group.
      if (!isMyTurn || (game.phase !== "acting" && !takenDiscardCard)) {
        errToast(t("game.err.cantAddNow"));
        return;
      }
      // A 4-card set without a Joker takes nothing more.
      if (dropData.locked) {
        errToast(t("game.err.groupFull"));
        return;
      }
      // Additions can be staged at any point of the turn (committed at discard
      // time with the first pose). Getting a Joker back needs the pose: made,
      // or composed up to the threshold.
      const canRecoverJoker = !!me?.has_played_nemna3 || thresholdMet;
      // What that pose still lacks: its points, or else the sequence without a
      // Joker (only missing when the room asks for one, see thresholdMet).
      const poseFirstForJoker = () => errToast(totalPendingPoints < (game.current_threshold || 0)
        ? t("game.err.poseFirstForJoker", { n: game.current_threshold })
        : t("game.err.pureSeqForJoker"));
      const targetCombo = game.players
        .find((p) => p.user_id === dropData.owner_id)
        ?.combinations?.find((c) => c.id === dropData.combo_id);
      const comboHasJoker = !!targetCombo?.cards?.some((c) => c.is_joker);
      const isThreeCardSet = targetCombo?.kind === "set" && targetCombo.cards.length === 3;
      // Server hand only: the staged Fausse card has to go into the first pose.
      const droppedCards = cardsToMove.map((id) => myHandRaw.find((c) => c.id === id));

      if (comboHasJoker && droppedCards.every((c) => c && !c.is_joker)) {
        if (isThreeCardSet && cardsToMove.length === 2) {
          // A 3-card set gives its Joker back for the 2 suits it lacks, dropped
          // together. Checked here: a refused /replace-joker costs a penalty.
          if (!freesSetJoker(targetCombo, droppedCards)) {
            errToast(t("game.err.replaceImpossible2"));
            return;
          }
          if (!canRecoverJoker) {
            poseFirstForJoker();
            return;
          }
          await swapJoker({
            ownerId: dropData.owner_id,
            comboId: dropData.combo_id,
            jokerId: targetCombo.cards.find((c) => c.is_joker).id,
            replacementId: cardsToMove[0],
            additionalId: cardsToMove[1],
          });
          return;
        }
        // One card on a 3-card set makes it a 4-card set: an addition, below.
        if (!isThreeCardSet) {
          // Sequence or 4-card set: the dropped card that fits a Joker, the one
          // under the finger first. A dry run, without penalty.
          for (const id of [cardId, ...cardsToMove.filter((x) => x !== cardId)]) {
            let fit = null;
            try {
              const { data } = await api.post(`/rooms/${roomId}/validate-joker-replacement`, {
                target_user_id: dropData.owner_id,
                combination_id: dropData.combo_id,
                replacement_card_id: id,
              });
              fit = data;
            } catch { /* the addition below gets its turn */ }
            if (!fit?.valid || !fit.joker_card_id) continue;
            if (!canRecoverJoker) {
              poseFirstForJoker();
              return;
            }
            await swapJoker({
              ownerId: dropData.owner_id,
              comboId: dropData.combo_id,
              jokerId: fit.joker_card_id,
              replacementId: id,
            });
            return;
          }
        }
      }

      // Otherwise the cards are added to the group.

      // Détection : drop d'un UNIQUE joker sur une SUITE → calculer les
      // positions d'insertion légales (côté backend, via `joker_slots`) :
      //   - deux positions valides → ouvrir le dialog gauche/droite
      //   - une seule valide → placement automatique immédiat
      //   - aucune valide → refus + message explicite (pas d'appel backend
      //     qui infligerait la sanction)
      const isSingleJokerDrop =
        cardsToMove.length === 1 &&
        myHandRaw.find((c) => c.id === cardsToMove[0])?.rank === "JOKER";
      const isSequenceTarget = targetCombo?.kind === "sequence";
      if (isSingleJokerDrop && isSequenceTarget) {
        // Rami Tounsia : insérer un Joker dans une suite posée est autorisé
        // - après Nemna3 (has_played_nemna3=true) → commit immédiat
        // - avant → staging (comme toute autre carte), commité à la défausse
        // The server's slots describe the group as it is on the table: a Joker staged on one of its
        // ends (pose being composed) closes that end too — two Jokers never side by side.
        const stagedOnCombo = pendingAdditions.find(
          (a) => a.target_user_id === dropData.owner_id && a.combination_id === dropData.combo_id
        );
        const stagedSides = Object.values(stagedOnCombo?.joker_sides || {});
        const slots = { ...(targetCombo?.joker_slots || { left: false, right: false }) };
        for (const side of stagedSides) slots[side] = false;
        const jokerCardId = cardsToMove[0];
        if (!slots.left && !slots.right) {
          const ends = targetCombo?.cards || [];
          const byJoker = stagedSides.length > 0 || ends[0]?.is_joker || ends[ends.length - 1]?.is_joker;
          errToast(t(byJoker ? "game.err.jokersSideBySide" : "game.err.jokerNoSlotBothEnds"));
          return;
        }
        if (slots.left && slots.right) {
          const leftCard  = slots.left_rank  ? { rank: slots.left_rank,  suit: slots.left_suit  } : null;
          const rightCard = slots.right_rank ? { rank: slots.right_rank, suit: slots.right_suit } : null;
          setJokerInsertChoice({
            ownerId: dropData.owner_id,
            comboId: dropData.combo_id,
            jokerCardId,
            stage: !me?.has_played_nemna3,
            leftCard,
            rightCard,
          });
          setSelected(new Set());
          return;
        }
        const autoPosition = slots.left ? "left" : "right";
        setSelected(new Set());
        // Flux STAGING : toujours utilisé quand le Nemna3 n'est pas encore validé.
        // Flux COMMIT : has_played_nemna3=true → commit immédiat via /add-to-combo.
        if (!me?.has_played_nemna3) {
          const _existingForJokerCombo = pendingAdditions.find(
            a => a.target_user_id === dropData.owner_id && a.combination_id === dropData.combo_id
          );
          const _jokerMergedIds = _existingForJokerCombo
            ? [..._existingForJokerCombo.card_ids, jokerCardId]
            : [jokerCardId];
          try {
            const { data } = await api.post(
              `/rooms/${roomId}/validate-addition`,
              {
                target_user_id: dropData.owner_id,
                combination_id: dropData.combo_id,
                card_ids: _jokerMergedIds,
              }
            );
            if (!data.valid) {
              errToast(data.error || t("game.err.jokerInvalidSeq"));
              return;
            }
          } catch (e) {
            errToast(e?.response?.data?.detail || t("game.err.validation"));
            return;
          }
          setPendingAdditions((prev) => {
            const idx = prev.findIndex(
              (a) =>
                a.target_user_id === dropData.owner_id
                && a.combination_id === dropData.combo_id
            );
            if (idx === -1) {
              return [
                ...prev,
                {
                  target_user_id: dropData.owner_id,
                  combination_id: dropData.combo_id,
                  card_ids: [jokerCardId],
                  joker_position: autoPosition,
                  joker_sides: { [jokerCardId]: autoPosition },
                },
              ];
            }
            const merged = [...prev];
            // The server is told a side for one Joker only: with two, it lays them out (apart).
            // `joker_sides` keeps each staged Joker's end, for the table and for the next Joker.
            const jokersStaged = merged[idx].card_ids.some((id) => myHandRaw.find((c) => c.id === id)?.is_joker);
            merged[idx] = {
              ...merged[idx],
              card_ids: [...merged[idx].card_ids, jokerCardId],
              joker_position: jokersStaged ? null : autoPosition,
              joker_sides: { ...(merged[idx].joker_sides || {}), [jokerCardId]: autoPosition },
            };
            return merged;
          });
          feed(t(autoPosition === "left" ? "game.ok.jokerLeftPending" : "game.ok.jokerRightPending"), "success");
          haptic.light();
          return;
        }
        // Flux COMMIT (has_played_nemna3) — commit immédiat via /add-to-combo.
        await guardedAction(async () => {
          try {
            const { data } = await api.post(`/rooms/${roomId}/add-to-combo`, {
              target_user_id: dropData.owner_id,
              combination_id: dropData.combo_id,
              card_ids: [jokerCardId],
              joker_position: autoPosition,
            });
            if (data.success) {
              _trackCommittedAddition(dropData.owner_id, dropData.combo_id, [jokerCardId]);
              feed(t(autoPosition === "left" ? "game.ok.jokerLeftAuto" : "game.ok.jokerRightAuto"), "success");
              haptic.success(); sound.combo();
            } else {
              errToast(data.message);
            }
          } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
        });
        return;
      }

      // STAGING path — used when Nemna3 not yet played. Committed atomically
      // at /discard alongside any pending groups. We dry-run via /validate-addition first.
      // COMMIT path — has_played_nemna3=true → immediate commit via /add-to-combo.
      if (!me?.has_played_nemna3) {
        const _existingForCombo = pendingAdditions.find(
          a => a.target_user_id === dropData.owner_id && a.combination_id === dropData.combo_id
        );
        const _mergedCardIds = _existingForCombo
          ? [..._existingForCombo.card_ids, ...cardsToMove]
          : cardsToMove;
        try {
          const { data } = await api.post(
            `/rooms/${roomId}/validate-addition`,
            {
              target_user_id: dropData.owner_id,
              combination_id: dropData.combo_id,
              card_ids: _mergedCardIds,
            }
          );
          if (!data.valid) {
            errToast(data.error || t("game.err.additionInvalid"));
            return;
          }
        } catch (e) {
          errToast(e?.response?.data?.detail || t("game.err.validation"));
          return;
        }
        // Merge with an existing staged addition on the same combo (so the
        // user can drop multiple cards on the same group one by one).
        setPendingAdditions((prev) => {
          const idx = prev.findIndex(
            (a) =>
              a.target_user_id === dropData.owner_id
              && a.combination_id === dropData.combo_id
          );
          if (idx === -1) {
            return [
              ...prev,
              {
                target_user_id: dropData.owner_id,
                combination_id: dropData.combo_id,
                card_ids: cardsToMove,
              },
            ];
          }
          const merged = [...prev];
          merged[idx] = {
            ...merged[idx],
            card_ids: [...merged[idx].card_ids, ...cardsToMove],
          };
          return merged;
        });
        setSelected(new Set());
        feed(t("game.ok.pendingAdd", { n: cardsToMove.length }), "success");
        haptic.light();
        return;
      }

      // Standard case: commit immediately via /add-to-combo.
      // Garde-fou : si le combo cible contient un Joker que la carte ne
      // remplace pas (essai ci-dessus), on refuse PROPREMENT si l'addition
      // est elle aussi invalide. Évite la double peine "swap impossible +
      // pénalité d'ajout".
      if (comboHasJoker) {
        try {
          const { data: va } = await api.post(
            `/rooms/${roomId}/validate-addition`,
            {
              target_user_id: dropData.owner_id,
              combination_id: dropData.combo_id,
              card_ids: cardsToMove,
            }
          );
          if (!va.valid) {
            errToast(va.error || t("game.err.neitherReplaceNorExtend"));
            return;
          }
        } catch {
          // Si la dry-run échoue (réseau), on continue vers le commit standard.
        }
      }
      await guardedAction(async () => {
        try {
          const { data } = await api.post(`/rooms/${roomId}/add-to-combo`, {
            target_user_id: dropData.owner_id,
            combination_id: dropData.combo_id,
            card_ids: cardsToMove,
          });
          if (data.success) {
            _trackCommittedAddition(dropData.owner_id, dropData.combo_id, cardsToMove);
            feed(t("game.ok.cardsAdded", { n: cardsToMove.length }), "success");
            haptic.success(); sound.combo();
            setSelected(new Set());
          } else {
            errToast(data.message);
          }
        } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
      });
      return;
    }

    if (dropData.type === "new-group") {
      if (!isMyTurn || (game.phase !== "acting" && !takenDiscardCard)) {
        errToast(t("game.err.cantPoseNow"));
        return;
      }
      if (cardsToMove.length < 3) { errToast(t("game.err.min3")); return; }
      // Auto-détection : si kind="auto" (zone unifiée), on tente d'abord la
      // suite, puis le brelan, et on garde celle qui valide. Pour les zones
      // legacy avec kind explicite, on conserve l'ancien comportement.
      const explicitKind = dropData.kind && dropData.kind !== "auto" ? dropData.kind : null;
      const kindsToTry = explicitKind ? [explicitKind] : ["sequence", "set"];
      let lastError = t("game.err.invalidCombo");
      let validated = null;
      for (const kind of kindsToTry) {
        try {
          const { data } = await api.post(`/rooms/${roomId}/validate-combination`, {
            kind,
            card_ids: cardsToMove,
            include_discard_top: !!takenDiscardCard,
          });
          if (data.valid) {
            validated = { kind, points: data.points };
            break;
          }
          // The run's reason wins when it is the Jokers' rule: the set tried next would only say
          // its cards are not of one rank.
          if (data.error && lastError !== JOKERS_SIDE_BY_SIDE) lastError = data.error;
        } catch (e) {
          lastError = e?.response?.data?.detail || t("game.err.validation");
        }
      }
      if (!validated) {
        errToast(lastError);
        return;
      }
      const alreadyStaged = pendingGroups.reduce((s, g) => s + g.card_ids.length, 0);
      if (alreadyStaged + cardsToMove.length > 14) {
        errToast(t("game.err.max14Cancelled", { n: alreadyStaged + cardsToMove.length }));
        return;
      }
      // For new sequences: if a single joker lands at an extremity (no internal gap
      // to fill), ask the user which end it represents before staging the group.
      if (validated.kind === "sequence") {
        const _pool = takenDiscardCard ? [...myHandRaw, takenDiscardCard] : myHandRaw;
        const _groupCards = cardsToMove.map(id => _pool.find(c => c.id === id)).filter(Boolean);
        const _jokers = _groupCards.filter(c => c.rank === "JOKER");
        if (_jokers.length === 1) {
          const _jokerCard = _jokers[0];
          // The real cards already follow each other (null when the Joker fills a
          // gap): the Joker goes at an end, and only the open ends are offered.
          const _run = layoutSequence(_groupCards.filter(c => c.rank !== "JOKER"));
          const _ends = _run ? sequenceJokerEnds(_run) : { left: null, right: null };
          if (_ends.left || _ends.right) {
            const _leftValid  = !!_ends.left;
            const _rightValid = !!_ends.right;
            const _suit = _run.find(c => c.suit)?.suit ?? null;
            const _runIds   = _run.map(c => c.id);
            const _leftIds  = [_jokerCard.id, ..._runIds];
            const _rightIds = [..._runIds, _jokerCard.id];
            if (_leftValid && _rightValid) {
              setJokerInsertChoice({
                newPendingGroup: true,
                pendingCardIdsLeft:  _leftIds,
                pendingCardIdsRight: _rightIds,
                pendingPointsLeft:   validated.points,
                pendingPointsRight:  validated.points,
                leftCard:  _suit ? { rank: _ends.left,  suit: _suit } : null,
                rightCard: _suit ? { rank: _ends.right, suit: _suit } : null,
              });
              setSelected(new Set());
              return;
            }
            // Only one end valid — auto-place, no dialog
            const _chosenIds = _leftValid ? _leftIds : _rightIds;
            setPendingGroups([...pendingGroups, { kind: "sequence", card_ids: _chosenIds, points: validated.points }]);
            setSelected(new Set());
            return;
          }
        }
      }

      // Real cards first, whatever order they were picked in: Jokers left over
      // after the gaps go on the right when there is room (layoutSequence).
      const isJokerId = (id) => (myHandRaw.find((c) => c.id === id)?.is_joker ? 1 : 0);
      setPendingGroups([
        ...pendingGroups,
        {
          kind: validated.kind,
          card_ids: sortGroupIds(validated.kind, [...cardsToMove].sort((a, b) => isJokerId(a) - isJokerId(b))),
          points: validated.points,
        },
      ]);
      setSelected(new Set());
    }
  };

  // Click on pioche card (only in drawing phase)
  const clickPioche = () => {
    if (!isMyTurn) { errToast(t("game.err.notYourTurn")); return; }
    if (game.phase !== "drawing") { errToast(t("game.err.alreadyDrew")); return; }
    triggerFly("pioche-pile", "player-hand", null, "draw");
    handleDrawPile();
  };

  // === Joker insert into sequence: left/right choice handler ===
  // Cas 0 — groupe pendant : met à jour directement pendingGroups.
  // Cas 1 — commit immédiat (has_played_nemna3) : POST /add-to-combo.
  // Cas 2 — staging (choice.stage=true) : on ajoute au pendingAdditions ;
  //          le /discard final brute-forcera l'ordre côté backend.
  const handleJokerInsertChoice = async (position) => {
    const choice = jokerInsertChoice;
    setJokerInsertChoice(null);
    if (!choice) return;
    // New pending group with joker at extremity — add it now that the user chose a side.
    if (choice.newPendingGroup) {
      const chosenIds    = position === "left" ? choice.pendingCardIdsLeft  : choice.pendingCardIdsRight;
      const chosenPoints = choice.pendingPointsLeft;
      setPendingGroups((prev) => [...prev, { kind: "sequence", card_ids: chosenIds, points: chosenPoints }]);
      setSelected(new Set());
      feed(t(position === "left" ? "game.ok.jokerLeft" : "game.ok.jokerRight"), "success");
      haptic.light();
      return;
    }
    if (choice.pendingGroupIdx !== undefined) {
      const chosenIds    = position === "left" ? choice.pendingCardIdsLeft  : choice.pendingCardIdsRight;
      const chosenPoints = position === "left" ? choice.pendingPointsLeft   : choice.pendingPointsRight;
      // Use chosenIds directly — do NOT re-sort. sortGroupCards moves Jokers to the end,
      // which would silently swap a left-Joker choice (lower rank) for a right-Joker (higher rank).
      setPendingGroups((prev) => prev.map((g, i) =>
        i === choice.pendingGroupIdx ? { ...g, card_ids: chosenIds, points: chosenPoints } : g
      ));
      feed(t(position === "left" ? "game.ok.jokerLeft" : "game.ok.jokerRight"), "success");
      haptic.light();
      return;
    }
    if (choice.stage) {
      // Dry-run via /validate-addition before staging.
      try {
        const { data } = await api.post(
          `/rooms/${roomId}/validate-addition`,
          {
            target_user_id: choice.ownerId,
            combination_id: choice.comboId,
            card_ids: [choice.jokerCardId],
          }
        );
        if (!data.valid) {
          errToast(data.error || t("game.err.jokerInvalidSeq"));
          return;
        }
      } catch (e) {
        errToast(e?.response?.data?.detail || t("game.err.validation"));
        return;
      }
      setPendingAdditions((prev) => {
        const idx = prev.findIndex(
          (a) =>
            a.target_user_id === choice.ownerId
            && a.combination_id === choice.comboId
        );
        if (idx === -1) {
          return [
            ...prev,
            {
              target_user_id: choice.ownerId,
              combination_id: choice.comboId,
              card_ids: [choice.jokerCardId],
              joker_position: position,
              joker_sides: { [choice.jokerCardId]: position },
            },
          ];
        }
        const merged = [...prev];
        const jokersStaged = merged[idx].card_ids.some((id) => myHandRaw.find((c) => c.id === id)?.is_joker);
        merged[idx] = {
          ...merged[idx],
          card_ids: [...merged[idx].card_ids, choice.jokerCardId],
          joker_position: jokersStaged ? null : position,
          joker_sides: { ...(merged[idx].joker_sides || {}), [choice.jokerCardId]: position },
        };
        return merged;
      });
      feed(t(position === "left" ? "game.ok.jokerLeftPending" : "game.ok.jokerRightPending"), "success");
      haptic.light();
      return;
    }
    await guardedAction(async () => {
      try {
        const { data } = await api.post(`/rooms/${roomId}/add-to-combo`, {
          target_user_id: choice.ownerId,
          combination_id: choice.comboId,
          card_ids: [choice.jokerCardId],
          joker_position: position,
        });
        if (data.success) {
          feed(t(position === "left" ? "game.ok.jokerLeft" : "game.ok.jokerRight"), "success");
          haptic.success(); sound.combo();
        } else {
          errToast(data.message);
        }
      } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
    });
  };

  const handleJokerInsertCancel = () => {
    setJokerInsertChoice(null);
  };

  // Auto-Nefrech: when only jokers remain in visible hand after staging, commit
  // automatically without requiring the player to drag a joker to the discard pile.
  // Same when the turn began by taking the discard pile's card before a first
  // pose: that take is only staged here (the server still says "drawing") and
  // this same request commits it (take_discard_first), as a dragged discard does.
  useEffect(() => {
    if (!isMyTurn || !game) return;
    const stagedCardId = takenDiscardCard?.id;
    if (game.phase !== "acting" && !(game.phase === "drawing" && stagedCardId)) return;
    if (myHand.length === 0) return;
    if (!myHand.every(c => c.is_joker || c.rank === "JOKER")) return;
    // Nefrech bypasses threshold on the backend when all non-joker cards are staged.
    // We only need at least one pending group (or already played Nemna3).
    const canNefrech = me?.has_played_nemna3 || pendingGroups.length > 0;
    if (!canNefrech) return;
    const jokerToDiscard = myHand[0];
    const groupsForServer = pendingGroups.map(g => ({ kind: g.kind, card_ids: g.card_ids }));
    const additionsForServer = pendingAdditions.map(a => ({
      target_user_id: a.target_user_id,
      combination_id: a.combination_id,
      card_ids: a.card_ids,
      joker_position: a.joker_position || null,
    }));
    const replacementsForServer = pendingReplacements.map(r => ({
      target_user_id: r.target_user_id,
      combination_id: r.combination_id,
      joker_card_id: r.joker_card_id,
      replacement_card_id: r.replacement_card_id,
      additional_card_id: r.additional_card_id || null,
    }));
    guardedAction(async () => {
      let succeeded = false;
      let serverPenalty = 0;
      try {
        const { data } = await api.post(`/rooms/${roomId}/discard`, {
          card_id: jokerToDiscard.id,
          combinations: groupsForServer,
          additions: additionsForServer,
          replacements: replacementsForServer,
          take_discard_first: !!stagedCardId,
          auto: true, // the player did not discard: a pose refused is not charged
        });
        serverPenalty = data.penalty || 0;
        if (data.success) {
          succeeded = true;
          setSelected(new Set());
          resetSelection();
          setTakenDiscardCard(null);
          feed(t("game.ok.autoNefrech"), "event");
          haptic.success(); sound.combo();
        } else {
          errToast(data.message);
        }
      } catch (e) { errToast(e?.response?.data?.detail || t("common.error")); }
      // As after a dragged discard: on failure the backend has given the taken
      // card back, so the staging is cleared.
      if (!succeeded && stagedCardId) {
        setTakenDiscardCard(null);
        setPendingGroups([]);
        setPendingAdditions([]);
        setPendingReplacements([]);
        setRecoveredJokers([]);
        resetSelection();
        await afterRefusedTake(stagedCardId, groupsForServer, additionsForServer, replacementsForServer, serverPenalty);
      }
    });
  }, [isMyTurn, myHand, pendingGroups, pendingAdditions, me?.has_played_nemna3, thresholdMet, game?.phase, takenDiscardCard]); // eslint-disable-line

  if (loading || !state || !game) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300">
        {t("game.loading")}
      </div>
    );
  }

  // The dealer's turn hint. A first pose being composed says what it still
  // lacks (points, then a pure sequence). One card left apart from Jokers:
  // discarding it leaves only Jokers or nothing and wins the round — the
  // threshold does not count then, the pure sequence does.
  const firstPose = !me?.has_played_nemna3 && pendingGroups.length > 0;
  const lacksPureSeq = firstPose && game.require_pure_sequence && !pendingHasPureSeq;
  const missingPts = firstPose ? Math.max(0, (game.current_threshold || 0) - totalPendingPoints) : 0;
  const lastCardLeft = myHand.filter((c) => !c.is_joker && c.rank !== "JOKER").length === 1;
  const turnHint = !isMyTurn ? null
    : waitingToDraw
    ? { text: canTakeDiscard ? t("game.hint.draw") : t("game.hint.drawOnly"), type: "drawing" }
    : lacksPureSeq && (lastCardLeft || missingPts === 0)
    ? { text: t("game.hint.needPureSeq", { n: pendingGroups.length }), type: "acting" }
    : lastCardLeft
    ? { text: t("game.hint.lastCard"), type: "acting_pending" }
    : missingPts > 0
    ? { text: t("game.hint.belowThreshold", { n: pendingGroups.length, missing: missingPts }), type: "acting" }
    : pendingGroups.length > 0
    ? { text: t("game.hint.pending", { n: pendingGroups.length }), type: "acting_pending" }
    : { text: canPose ? t("game.hint.act") : t("game.hint.discardOnly"), type: "acting" };

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={() => { setActiveDrag(null); setActiveDropComboId(null); }}>
      {/* The board never mirrors: card order and left/right mean the same in every language. */}
      <div dir="ltr" className="h-[100dvh] bg-slate-950 text-white flex flex-col overflow-hidden game-container safe-left safe-right">
        {/* The ad's strip: the native banner (320 × 50, centred) is drawn over it, on top of the
            web view. Kept from the start so the board does not jump down when the ad comes, and kept
            when none comes: an empty band of this player's table felt (.rami-ad-slot, owner's request). */}
        {bannerHeight > 0 && (
          <div
            aria-hidden="true"
            className="rami-ad-slot shrink-0 border-b border-white/5"
            style={{ ...matStyle(tableThemeId), height: bannerHeight + 1 }}
          />
        )}
        <GameTopBar
          game={game}
          me={me}
          pendingGroups={pendingGroups}
          totalPendingPoints={totalPendingPoints}
          muted={muted}
          toggleMute={toggleMute}
          wsRef={wsRef}
          wsVersion={wsVersion}
          user={user}
          onExit={handleBackToLobby}
          voiceSpeaking={voiceSpeaking}
          onVoiceSpeakingChange={setVoiceSpeaking}
          onVoiceLinksChange={setVoiceLinks}
        />

        {/* Main table area, in the colour this player picked in the waiting room */}
        <div ref={matRef} className="flex-1 rami-mat relative flex flex-col min-h-0" style={matStyle(tableThemeId)}>
          {/* My vote: the table goes dark, the hand and its bar stay in the light (z-40, below). */}
          <AnimatePresence>
            {showVote && (
              <motion.div
                key="redeal-scrim"
                className="redeal-scrim absolute inset-0 z-30 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>
          <OpponentsRow
            opponents={opponents}
            clock={turnClock}
            currentPlayerId={game.current_player_id}
            continuerId={game.redeal_continuer_id}
            playerColorMap={playerColorMap}
            isHost={room?.host_user_id === user?.user_id}
            onReplaceWithBot={async (targetUserId) => {
              try {
                await api.post(`/rooms/${roomId}/players/${targetUserId}/replace-bot`);
              } catch (e) {
                errToast(e?.response?.data?.detail || t("common.error"));
              }
            }}
          />
          {/* The pose another player is composing, with their name, centred under the players' pills (owner's
              request). It takes its room: the table's groups move down under it, as they did when it sat under
              the player's pill, instead of being hidden. The pills above do not move. Empty: no room. */}
          <div className="shrink-0 flex justify-center px-2">
            <PosePreview players={game.players} myId={user?.user_id} playerColorMap={playerColorMap} />
          </div>

          {/* Grid 2 colonnes : Cartes sur table | Score+Pioche (ordre selon préférence pioche_side) */}
          {(() => {
            const piocheLeft = (localStorage.getItem("pioche_side") || "right") === "left";

            // La col pioche est définie une seule fois et placée avant ou après la col table
            // selon la préférence. On utilise l'ordre source (pas gridColumn) pour éviter
            // tout comportement différent de h-full / alignSelf entre les deux modes.
            const piocheCol = (
              // On a phone the column sits at the bottom of the table, a bit lifted (scores, pioche, discard
              // pile), over the table's whole width: the groups flow around it (lib/tableRoom.js).
              <div
                ref={pileColumnRef}
                data-testid="pile-column"
                className={phoneBoard
                  ? `absolute z-10 flex flex-col gap-1.5 ${piocheLeft ? "left-1" : "right-1"}`
                  : "flex flex-col gap-1.5 self-start"}
                style={phoneBoard ? { bottom: COLUMN_LIFT + 4 } : undefined}
              >
                <ScoreboardOverlay players={game.players} currentPlayerId={game.current_player_id} phase={game.phase} voiceSpeaking={voiceSpeaking} voiceLinks={voiceLinks} elimScoreY={game.elim_score_y} />
                <PiocheArea
                  game={game}
                  me={me}
                  isMyTurn={isMyTurn}
                  takenDiscardCard={takenDiscardCard}
                  pendingGroups={pendingGroups}
                  pendingAdditions={pendingAdditions}
                  currentPlayerTookDiscard={
                    !isMyTurn &&
                    !!game.players.find(p => p.user_id === game.current_player_id)
                      ?.preview_state?.took_discard
                  }
                  canTakeDiscard={canTakeDiscard}
                  onClickPioche={clickPioche}
                  onTakeDiscard={handleTakeDiscard}
                  onCancelStaging={handleCancelStaging}
                />
              </div>
            );

            return (
              <div
                className="relative flex-1 min-h-0 grid gap-1 p-1"
                style={{
                  gridTemplateColumns: phoneBoard ? "1fr" : piocheLeft ? "auto 1fr" : "1fr auto",
                  gridTemplateRows: "1fr",
                  alignItems: "start",
                }}
              >
                {piocheLeft && piocheCol}

                {/* Col table — toujours dans la colonne 1fr, placée en 1er ou 2e selon l'ordre source */}
                <motion.div layoutScroll ref={tableScrollRef} className="overflow-y-auto overflow-x-hidden relative h-full" style={{ alignSelf: "stretch" }}>
                  {/* A player out of the vote (eliminated): a small capsule at the top of the table. */}
                  <AnimatePresence>
                    {game.phase === "redeal_vote" && !inVote && (
                      <div key="redeal-capsule" className="absolute top-2 inset-x-0 z-20 flex justify-center pointer-events-none">
                        <RedealVoteCapsule game={game} me={me} playerColorMap={playerColorMap} />
                      </div>
                    )}
                  </AnimatePresence>

                  {tableRoom && (
                    <>
                      <div aria-hidden="true" style={{ float: piocheLeft ? "left" : "right", width: 0, height: tableRoom.top }} />
                      <div
                        aria-hidden="true"
                        data-testid="table-column-room"
                        style={{ float: piocheLeft ? "left" : "right", clear: piocheLeft ? "left" : "right", width: floatWidth(tableRoom, piocheLeft), height: tableRoom.height }}
                      />
                    </>
                  )}

                  <div ref={tableListRef} style={tableRoom ? { "--table-widest": tableRoom.widest } : undefined}>
                  <TableCombinations
                    game={tableGame}
                    me={me}
                    isMyTurn={isMyTurn}
                    takenDiscardCard={takenDiscardCard}
                    pendingGroups={pendingGroups}
                    pendingAdditions={pendingAdditions}
                    pendingReplacements={pendingReplacements}
                    recoveredJokers={recoveredJokers}
                    committedAdditions={committedAdditions}
                    myHandRaw={myHandRaw}
                    playerColorMap={playerColorMap}
                    activeDropComboId={activeDropComboId}
                    onRemoveGroup={removeGroup}
                    onAddToPendingGroup={() => {}}
                    onRemovePendingAddition={(idx) => {
                      setPendingAdditions((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    onRemovePendingReplacement={(idx) => {
                      setPendingReplacements((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    onUndoAllComboChanges={handleUndoAllComboChanges}
                    room={tableRoom}
                  />
                  </div>
                </motion.div>

                {!piocheLeft && piocheCol}
              </div>
            );
          })()}

          {/* In the vote, up in the middle of the table with the vote above it (voteLift).
              Click-through but for the bar, the cards and the vote: it sits over the table (z-40), and the
              pioche column runs under it on a low window (the discard pile's card could only be taken
              by its top). */}
          <motion.div
            ref={bottomRef}
            className="shrink-0 safe-bottom pb-1 px-1 relative z-40 pointer-events-none"
            initial={false}
            animate={{ y: showVote ? -voteLift : 0 }}
            transition={{ duration: reduceMotion ? 0 : VOTE_SLIDE_S, ease: [0.4, 0, 0.2, 1] }}
          >
            <AnimatePresence>
              {showVote && (
                <div
                  key="redeal-sheet"
                  ref={voteSheetRef}
                  className="absolute bottom-full inset-x-0 px-3 flex justify-center [&>*]:pointer-events-auto"
                  style={{ marginBottom: -VOTE_SHEET_OVERLAP }}
                >
                  <RedealVoteSheet
                    game={game}
                    me={me}
                    playerColorMap={playerColorMap}
                    onVote={handleVoteRedeal}
                    loading={voting}
                    clock={turnClock}
                    myTurn={myVoteTurn}
                    handSize={myHand.length}
                    endedBy={voteEndedBy}
                    onOk={() => setVoteOkRound(voteRound)}
                  />
                </div>
              )}
            </AnimatePresence>
            <BottomActions
              user={user}
              me={me}
              myHandRaw={myHand}
              isMyTurn={isMyTurn}
              clock={myClock}
              isContinuer={game.redeal_continuer_id === user?.user_id}
              sortMode={sortMode}
              onSortChange={setSortMode}
              myColor={playerColorMap?.[user?.user_id]}
              emojiButton={EMOJI_REACTIONS && <EmojiPicker onPick={emojis.send} disabled={!!emojis.shown[user?.user_id]} />}
              aboveBadge={
                // Out of the way while the vote sits above the hand.
                <div style={{ opacity: feedHidden ? 0 : 1, visibility: feedHidden ? "hidden" : "visible", transition: "opacity .3s, visibility .3s" }}>
                  <GameFeed
                    ref={feedRef}
                    actionLog={state?.game?.last_action_log}
                    roundNumber={game.round_number}
                    turnHint={turnHint}
                  />
                </div>
              }
            />
<PlayerHand myHand={myHand} selected={selected} onToggleSelect={toggleSelect} activeDrag={activeDrag} isDealing={isDealing} dealingKey={dealingKey} spotlight={showVote} />
          </motion.div>
        </div>

        <DragOverlay>
          {activeDrag && (() => {
            const isGroupDrag = selected.has(activeDrag.id) && selected.size > 1;
            if (!isGroupDrag) {
              return <PlayingCard card={activeDrag} size="md" disabled />;
            }
            // Mini-stack : les 2 premières cartes du groupe derrière la carte draguée
            const others = myHand
              .filter((c) => selected.has(c.id) && c.id !== activeDrag.id)
              .slice(0, 2);
            return (
              <div className="relative" style={{ width: "fit-content" }}>
                {[...others].reverse().map((card, i) => (
                  <div
                    key={card.id}
                    className="absolute"
                    style={{ top: `${-(i + 1) * 3}px`, left: `${(i + 1) * 6}px`, opacity: 0.6, zIndex: i }}
                  >
                    <PlayingCard card={card} size="md" disabled />
                  </div>
                ))}
                <div style={{ position: "relative", zIndex: others.length }}>
                  <PlayingCard card={activeDrag} size="md" disabled />
                </div>
                <div
                  className="absolute -top-2 -right-2 bg-amber-500 text-slate-950 text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center"
                  style={{ zIndex: others.length + 1 }}
                >
                  {selected.size}
                </div>
              </div>
            );
          })()}
        </DragOverlay>

        <RoundSummaryDialog
          open={showRoundSummary && !(game?.phase === "game_over" && gameWinners(game).includes(user?.user_id))}
          onOpenChange={() => {}}
          game={game}
          room={room}
          user={user}
          myUserId={user?.user_id}
          handOrder={myHand.map((c) => c.id)}
          onNextRound={handleNextRound}
          onBackToLobby={handleBackToLobby}
          onRematch={handleRematch}
          rematching={rematching}
          rematchHint={rematchHint}
        />

        <WinnerCelebration
          game={game}
          me={me}
          onBackToLobby={handleBackToLobby}
          loading={backingToLobby}
          onRematch={handleRematch}
          rematching={rematching}
          rematchHint={rematchHint}
        />

        <JokerInsertDialog
          open={!!jokerInsertChoice}
          onCancel={handleJokerInsertCancel}
          onChoose={handleJokerInsertChoice}
          leftCard={jokerInsertChoice?.leftCard}
          rightCard={jokerInsertChoice?.rightCard}
        />

        <CardFlyLayer animations={flyCards} />
        <BrokenSetsLayer run={brokenSetsRun} topCard={game?.discard_top} />
        <EmojiLayer shown={emojis.shown} myId={user?.user_id} />

        {/* How to play: opens by itself at the start of the player's first game, never again */}
        {TUTORIAL && <FirstGameTutorial ready={!!game && !showRoundSummary} />}

      </div>
    </DndContext>
  );
}
